import {
  LEARN_OG_IMAGE,
  canonicalLearnUrl,
  normalizeLearnContentPath,
  privateLearnRoute,
} from "../utils/learnRoutes";
import { buildNextHundredLearnPages } from "./learnNextHundredPages";
import { buildThirdHundredLearnPages } from "./learnThirdHundredPages";
import { BOOK_LEARN_NAVIGATION_GROUPS, buildBookLearnPages } from "./learnBookPages";

const LAST_UPDATED = "2026-07-22";

const route = (path) => privateLearnRoute(path);
const canonical = (path) => canonicalLearnUrl(path);

const baseCrumbs = [{ label: "Aprender", path: route() }];

const PUBLIC_COPY_REPLACEMENTS = [
  [/Unity\/WebGL/gi, "tablero del juego en el navegador"],
  [/Unity WebGL/gi, "tablero del juego en el navegador"],
  [/Unity no carga/gi, "El tablero no carga"],
  [/WebGL no compatible/gi, "El navegador no carga el tablero"],
  [/WebGL movil/gi, "Carga del tablero en movil"],
  [/\bWebGL\b/gi, "graficos del navegador"],
  [/\bUnity\b/gi, "tablero del juego"],
  [/\bReact\b/gi, "la pagina web"],
  [/frontend\/backend/gi, "la plataforma"],
  [/\bfrontend\b/gi, "la plataforma"],
  [/\bbackend\b/gi, "el servidor"],
  [/\bAPI\b/gi, "servicio"],
  [/\bendpoints?\b/gi, "servicios internos"],
  [/\bsockets?\b/gi, "conexion en vivo"],
  [/\blocalStorage\b/gi, "memoria del navegador"],
  [/\blayout\b/gi, "diseño de pantalla"],
  [/\bUI\b/g, "interfaz"],
  [/\brenderiza\b/gi, "muestra"],
  [/\brenderizar\b/gi, "mostrar"],
  [/\brenderizado\b/gi, "mostrado"],
  [/\bscripts\b/gi, "funciones"],
  [/\bscript\b/gi, "funcion"],
  [/\bvoiceControl\b/g, "control por voz"],
  [/\bVoiceCameraBridge\b/g, "control de camara por voz"],
  [/\bCameraOrbit\b/g, "control de camara"],
  [/\bDiceUI\b/g, "panel de dados"],
  [/\bCienzarBot\b/g, "bot de Cienzar"],
  [/\bPlayBot\b/g, "Jugar con bot"],
  [/\bRegisterForm\b/g, "formulario de registro"],
  [/\bGameLayout\b/g, "menu principal"],
  [/\bMatchHistory\b/g, "historial de partidas"],
  [/\bPM2\b/g, "servidor"],
  [/\bmodo free\b/gi, "Modo Ciencia"],
  [/\bmodo dice\b/gi, "Modo Azar"],
  [/\bSciluck\b/g, "Cienzar"],
  [/el navegador lo solicita/gi, "aparezca la solicitud de permiso"],
  [/si el navegador lo solicita/gi, "cuando aparezca la solicitud de permiso"],
  [/interfaz compatible/gi, "pantalla donde aparece la opcion"],
  [/Procesando comando/gi, "mientras XIMA termina de escuchar"],
  [/Contenido verificado con puntos pendientes/gi, "Guia de referencia"],
  [/Requiere confirmacion del propietario antes de ampliar/gi, "Guia de referencia"],
  [/requiere confirmacion del propietario/gi, "se explica desde las referencias visibles del proyecto"],
  [/requieren confirmacion del propietario/gi, "se explican desde las referencias visibles del proyecto"],
  [/debe confirmarse con el propietario/gi, "se interpreta desde las referencias visibles del proyecto"],
  [/hasta que el propietario confirme/gi, "segun las referencias visibles del proyecto"],
  [/confirmacion del propietario/gi, "referencia visible del proyecto"],
  [/confirmacion humana/gi, "informacion publica disponible"],
  [/confirmacion pendiente/gi, "referencia visible"],
  [/pendiente de aprobacion publica final/gi, "presentado como marco teorico del libro"],
  [/pendiente de aprobacion/gi, "como referencia en desarrollo"],
  [/pendiente de confirmacion/gi, "como referencia visible"],
  [/pendiente de confirmar/gi, "por comprobar en la interfaz"],
  [/pendiente de validar/gi, "por comprobar en la interfaz"],
  [/estado noindex pendiente/gi, "referencia visible"],
  [/estado pendiente/gi, "referencia visible"],
  [/revision legal pendiente/gi, "condiciones visibles"],
  [/revision pendiente/gi, "referencia visible"],
  [/calendario pendiente/gi, "calendario por anunciar"],
  [/regla pendiente de confirmar/gi, "regla por comprobar"],
  [/nombre pendiente de validar/gi, "nombre por comprobar"],
  [/pagina noindex sobre/gi, "referencia sobre"],
  [/pagina prudente sobre/gi, "referencia sobre"],
  [/esta pagina no se indexa/gi, "esta referencia se muestra con cautela"],
  [/no se indexa/gi, "se muestra con cautela"],
  [/por que no se indexa\?/gi, "Por que aparece como referencia?"],
  [/por que noindex\?/gi, "Por que aparece como referencia?"],
  [/puntos pendientes/gi, "informacion relacionada"],
  [/marcada noindex/gi, "presentada como referencia"],
  [/marcado noindex/gi, "presentado como referencia"],
  [/noindex/gi, "referencia"],
  [/sitemap/gi, "indice publico"],
  [/calculadora oficial/gi, "lectura publica"],
  [/validacion tecnica/gi, "verificacion del sistema"],
  [/validacion fina/gi, "lectura fina"],
  [/debe validarse/gi, "debe compararse"],
  [/deben contrastarse/gi, "se contrastan"],
  [/aprobacion tecnica/gi, "verificacion del sistema"],
  [/aprobacion editorial/gi, "informacion publica"],
  [/aprobacion del propietario/gi, "informacion publica disponible"],
  [/aprobacion/gi, "informacion publica"],
  [/propietario/gi, "proyecto"],
];

const cleanPublicCopy = (value) => {
  if (typeof value !== "string") return value;
  return PUBLIC_COPY_REPLACEMENTS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value
  )
    .replace(/\s+/g, " ")
    .trim();
};

const cleanTextList = (items = []) => items.map(cleanPublicCopy);

const cleanSections = (sections = []) =>
  sections.map((section) => ({
    ...section,
    heading: cleanPublicCopy(section.heading),
    body: cleanPublicCopy(section.body),
  }));

const cleanFaqs = (faqs = []) =>
  faqs.map((item) => ({
    ...item,
    question: cleanPublicCopy(item.question),
    answer: cleanPublicCopy(item.answer),
  }));

const cleanLinks = (links = []) =>
  links.map((item) => ({
    ...item,
    label: cleanPublicCopy(item.label),
  }));

const cleanCta = (cta) =>
  cta
    ? {
        ...cta,
        label: cleanPublicCopy(cta.label),
      }
    : cta;

const page = ({
  slug,
  aliases = [],
  friendlyUrl,
  title,
  metaDescription,
  h1,
  intro,
  sections,
  stepsTitle = "",
  stepsNote = "",
  steps = [],
  examples = [],
  screenshots = [],
  faqs = [],
  internalLinks = [],
  cta,
  breadcrumbs,
  sources = [],
  status = "verified",
  structuredDataType = "Article",
  robots = "index,follow",
  publishedAt = LAST_UPDATED,
  ogImage = LEARN_OG_IMAGE,
}) => ({
  slug,
  aliases,
  path: route(slug),
  friendlyUrl,
  title: cleanPublicCopy(title),
  metaDescription: cleanPublicCopy(metaDescription),
  h1: cleanPublicCopy(h1),
  intro: cleanPublicCopy(intro),
  sections: cleanSections(sections),
  stepsTitle: cleanPublicCopy(stepsTitle),
  stepsNote: cleanPublicCopy(stepsNote),
  steps: cleanTextList(steps),
  examples: cleanTextList(examples),
  screenshots: cleanTextList(screenshots),
  faqs: cleanFaqs(faqs),
  internalLinks: cleanLinks(internalLinks),
  cta: cleanCta(cta),
  breadcrumbs: cleanLinks([...baseCrumbs, ...(breadcrumbs || [])]),
  canonicalUrl: friendlyUrl || canonical(slug),
  ogTitle: cleanPublicCopy(title),
  ogDescription: cleanPublicCopy(metaDescription),
  ogImage,
  robots,
  publishedAt,
  updatedAt: LAST_UPDATED,
  sources,
  status,
  structuredDataType,
});

export const LEARN_PAGE_STATUS = {
  verified: "Guia disponible",
  partial: "Guia de referencia",
  ownerConfirmation: "Guia en desarrollo",
};

const coreLearnSeoPages = [
  page({
    slug: "que-es-xima",
    friendlyUrl: canonical("que-es-xima"),
    title: "Que es XIMA | Aprende el objetivo y las bases del juego",
    metaDescription:
      "Aprende que es XIMA, cual es el objetivo del juego y como se relacionan el tablero, los bloques y la cima.",
    h1: "Que es XIMA",
    intro:
      "XIMA es un juego de tablero 3D con bloques. El objetivo principal verificado es llevar el bloque especial propio a la cima del tablero.",
    sections: [
      {
        heading: "Objetivo principal",
        body:
          "Cada jugador participa con bloques comunes y un bloque especial. La condicion central de victoria es que el bloque especial llegue a la cima en una jugada valida.",
      },
      {
        heading: "Como se juega",
        body:
          "Durante la partida, los jugadores incorporan bloques al tablero o mueven bloques que ya estan dentro. Las reglas de movimiento, altura, dominacion, captura y rescate definen que jugadas son validas.",
      },
      {
        heading: "Que incluye la plataforma",
        body:
          "La experiencia web de XIMA incluye lobby, modos de juego, partidas, historial, replay, estadisticas, ranking, amigos, tienda, soporte y la seccion Aprender.",
      },
    ],
    examples: [
      "Si tu bloque especial alcanza la cima, esa jugada puede cerrar la partida a tu favor.",
      "Si tu bloque especial fue capturado, un bloque comun puede participar en una jugada de rescate segun las reglas.",
    ],
    screenshots: ["Vista general de la pantalla Aprender", "Vista del tablero con la cima destacada"],
    faqs: [
      {
        question: "Cual es el objetivo de XIMA?",
        answer: "Llevar el bloque especial a la cima del tablero.",
      },
      {
        question: "XIMA usa solo azar?",
        answer:
          "No. El proyecto incluye modos con decision libre, modos con dados y modos hibridos. Cada modo se explica por separado.",
      },
    ],
    internalLinks: [
      { label: "Reglas de XIMA", path: route("reglas") },
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Bloques comunes y especiales", path: route("reglas/bloques") },
    ],
    cta: {
      label: "Ver reglas de XIMA",
      path: route("reglas"),
    },
    breadcrumbs: [{ label: "Que es XIMA", path: route("que-es-xima") }],
    sources: ["docs/xima-knowledge/que-es-xima.md"],
  }),
  page({
    slug: "como-jugar",
    friendlyUrl: canonical("como-jugar"),
    title: "Como jugar XIMA | Guia inicial para tu primera partida",
    metaDescription:
      "Aprende como jugar XIMA paso a paso: objetivo, bloques, turnos, movimientos, modos y primera partida online.",
    h1: "Como jugar XIMA",
    intro:
      "Para empezar en XIMA necesitas entender el objetivo, reconocer tus bloques, usar turnos validos y elegir una forma de partida.",
    sections: [
      {
        heading: "Empieza por el objetivo",
        body:
          "El objetivo verificado es llevar el bloque especial propio a la cima del tablero. Las demas reglas protegen que esa llegada ocurra mediante una jugada valida.",
      },
      {
        heading: "Conoce tus bloques",
        body:
          "Cada jugador usa bloques comunes y un bloque especial. Los bloques comunes permiten jugar, ocupar rutas, dominar, capturar y rescatar el especial capturado.",
      },
      {
        heading: "Juega tu turno",
        body:
          "En su turno, un jugador puede incorporar un bloque nuevo o mover un bloque propio que ya esta en el tablero. El movimiento debe respetar linea recta, tablero, alturas y modo elegido.",
      },
      {
        heading: "Elige una partida",
        body:
          "La plataforma permite crear partidas, unirse a partidas disponibles, jugar con bot, practicar en FreeBox y usar otros flujos visibles desde el producto.",
      },
    ],
    steps: [
      "Revisa el objetivo: llevar tu bloque especial a la cima.",
      "Identifica la diferencia entre bloque especial y bloques comunes.",
      "Elige un modo de juego, como Ciencia o Azar.",
      "Crea una partida o unete a una disponible.",
      "En cada turno, incorpora un bloque o mueve uno propio ya colocado.",
      "Usa el historial y el replay para revisar lo aprendido despues de jugar.",
    ],
    examples: [
      "Si aun no tienes bloques en el tablero, una jugada inicial puede ser incorporar un bloque.",
      "Si ya tienes un bloque colocado, puedes buscar un destino valido en linea recta.",
      "Si juegas Azar, los dados pueden formar parte de la decision de movimiento.",
    ],
    screenshots: [
      "Pantalla del lobby con acceso a modos y partidas",
      "Pantalla de crear partida con modo, jugadores y tiempo",
      "Vista de replay para estudiar una partida finalizada",
    ],
    faqs: [
      {
        question: "Que debo aprender primero?",
        answer:
          "Empieza por el objetivo, los bloques y los movimientos. Luego compara modos y juega una partida sencilla.",
      },
      {
        question: "Necesito saber todas las reglas antes de jugar?",
        answer:
          "No necesariamente. Puedes empezar con objetivo, turno e incorporacion, y despues profundizar en captura, rescate y replay.",
      },
      {
        question: "Puedo practicar sin competir?",
        answer:
          "El proyecto incluye opciones de practica como bot y FreeBox, pero cada flujo debe revisarse desde la interfaz antes de crear tutoriales avanzados.",
      },
    ],
    internalLinks: [
      { label: "Que es XIMA", path: route("que-es-xima") },
      { label: "Reglas de XIMA", path: route("reglas") },
      { label: "Crear una partida", path: route("tutoriales/crear-partida") },
      { label: "Historial y replay", path: route("historial-y-replay") },
    ],
    cta: {
      label: "Ver reglas de XIMA",
      path: route("reglas"),
    },
    breadcrumbs: [{ label: "Como jugar", path: route("como-jugar") }],
    sources: [
      "docs/xima-knowledge/reglas-generales.md",
      "docs/xima-knowledge/sistema-de-movimientos.md",
      "docs/xima-knowledge/creacion-y-union-a-partidas.md",
    ],
    status: "partial",
  }),
  page({
    slug: "reglas",
    friendlyUrl: canonical("reglas"),
    title: "Reglas de XIMA | Objetivo, bloques, movimiento y rescate",
    metaDescription:
      "Consulta las reglas verificadas de XIMA sobre objetivo, turnos, bloques, movimientos, dominacion, captura y rescate.",
    h1: "Reglas de XIMA",
    intro:
      "Las reglas de XIMA explican como iniciar una partida, como mover bloques y como alcanzar la cima con el bloque especial.",
    sections: [
      {
        heading: "Inicio y turnos",
        body:
          "Cada jugador empieza con sus bloques fuera del tablero. En su turno puede incorporar un bloque nuevo o mover un bloque propio que ya esta en juego.",
      },
      {
        heading: "Movimiento",
        body:
          "Los bloques se mueven en linea recta por direcciones horizontales, verticales o diagonales. En Modo Azar, los dados pueden indicar el avance.",
      },
      {
        heading: "Bloque especial y cima",
        body:
          "El bloque especial es la pieza clave. Si llega a la cima en una jugada valida, el jugador gana la partida.",
      },
      {
        heading: "Dominacion, captura y rescate",
        body:
          "Un bloque puede dominar piezas inferiores y, bajo las reglas publicas, producir capturas en una columna. Si el especial fue capturado, un bloque comun puede llegar a la cima para rescatarlo.",
      },
      {
        heading: "Estados avanzados del libro",
        body:
          "El libro Como llegar a la Xima amplia las reglas con conceptos como ahogado, sin recurso, posicion muerta e incremento de material. Estas guias avanzadas se enlazan por separado para no mezclar reglas basicas con teoria de estudio.",
      },
    ],
    examples: [
      "Una jugada puede consistir en incorporar un bloque comun al tablero.",
      "Una jugada tambien puede mover un bloque propio que ya estaba colocado.",
      "Una partida estancada puede requerir revisar incremento de material en vez de declararla empatada.",
    ],
    screenshots: ["Lista de reglas dentro de Aprender", "Ejemplo visual de bloque especial y bloque comun"],
    faqs: [
      {
        question: "Se puede salir del tablero despues de entrar?",
        answer: "No. La regla publica indica que los bloques no pueden moverse fuera del tablero despues de entrar.",
      },
      {
        question: "Un bloque comun puede llegar a la cima?",
        answer:
          "Solo en el contexto de rescate del bloque especial capturado, segun la regla publica verificada.",
      },
    ],
    internalLinks: [
      { label: "Bloques comunes y especiales", path: route("reglas/bloques") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
      { label: "Ahogado en XIMA", path: route("reglas/ahogado") },
      { label: "Incremento de material avanzado", path: route("reglas/incremento-material-avanzado") },
    ],
    cta: {
      label: "Aprender movimientos",
      path: route("reglas/movimientos"),
    },
    breadcrumbs: [{ label: "Reglas", path: route("reglas") }],
    sources: [
      "docs/xima-knowledge/reglas-generales.md",
      "frontend/public/rules/es/xima-rules.json",
      "frontend/public/learn/library.json#como-llegar-a-la-xima",
    ],
    status: "partial",
  }),
  page({
    slug: "reglas/bloques",
    friendlyUrl: canonical("reglas/bloques"),
    title: "Bloques de XIMA | Bloque especial y bloques comunes",
    metaDescription:
      "Aprende la diferencia entre bloque especial y bloques comunes en XIMA, incluyendo victoria, captura y rescate.",
    h1: "Bloques comunes y especiales",
    intro:
      "XIMA distingue entre bloques comunes y un bloque especial por jugador. Entender esa diferencia es esencial para jugar bien.",
    sections: [
      {
        heading: "Bloque especial",
        body:
          "El bloque especial define el objetivo de victoria. Cuando llega a la cima en una jugada valida, gana la partida.",
      },
      {
        heading: "Bloques comunes",
        body:
          "Los bloques comunes se incorporan, se mueven, pueden dominar, pueden participar en capturas y pueden usarse para rescatar el bloque especial capturado.",
      },
      {
        heading: "Rescate",
        body:
          "Si el bloque especial fue capturado, un bloque comun puede llegar a la cima para recuperarlo y permitir que vuelva al juego.",
      },
    ],
    examples: [
      "El bloque especial no debe pasar sobre otro bloque especial.",
      "Un bloque comun puede llegar a la cima solo para rescatar un especial capturado.",
    ],
    screenshots: ["Bloque especial destacado", "Bloques comunes de un jugador"],
    faqs: [
      {
        question: "Todos los bloques ganan al llegar a la cima?",
        answer: "No. La victoria principal corresponde al bloque especial.",
      },
      {
        question: "Para que sirven los bloques comunes?",
        answer: "Sirven para jugar, ocupar rutas, dominar, capturar y rescatar el especial capturado.",
      },
    ],
    internalLinks: [
      { label: "Reglas de XIMA", path: route("reglas") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
      { label: "Que es XIMA", path: route("que-es-xima") },
    ],
    cta: {
      label: "Ver como se mueven los bloques",
      path: route("reglas/movimientos"),
    },
    breadcrumbs: [
      { label: "Reglas", path: route("reglas") },
      { label: "Bloques", path: route("reglas/bloques") },
    ],
    sources: ["docs/xima-knowledge/bloques-comunes-y-especiales.md"],
  }),
  page({
    slug: "reglas/movimientos",
    friendlyUrl: canonical("reglas/movimientos"),
    title: "Movimientos en XIMA | Incorporar, mover y subir en el tablero",
    metaDescription:
      "Aprende como funcionan los movimientos en XIMA: incorporar bloques, mover en linea recta, respetar alturas y usar dados cuando aplica.",
    h1: "Sistema de movimientos en XIMA",
    intro:
      "El movimiento en XIMA combina incorporacion, desplazamiento en linea recta, alturas del tablero y reglas especiales segun el modo de juego.",
    sections: [
      {
        heading: "Incorporar o mover",
        body:
          "En su turno, un jugador puede incorporar un bloque nuevo al tablero o mover un bloque propio que ya esta en juego.",
      },
      {
        heading: "Linea recta",
        body:
          "Las reglas publicas indican movimiento en linea recta, con direcciones horizontales, verticales o diagonales.",
      },
      {
        heading: "Alturas",
        body:
          "El tablero tiene niveles. Los movimientos deben respetar las restricciones de altura indicadas por las reglas y validadas por Unity.",
      },
      {
        heading: "Dados",
        body:
          "En Modo Azar los dados pueden indicar el avance. Otros modos pueden bloquear o activar dados segun su logica.",
      },
    ],
    steps: [
      "Elige si vas a incorporar un bloque o mover uno que ya esta en el tablero.",
      "Selecciona el bloque propio.",
      "Elige un destino valido en linea recta.",
      "Comprueba que la altura y el modo permiten esa jugada.",
      "Confirma el movimiento.",
    ],
    screenshots: ["Seleccion de bloque", "Destinos disponibles en el tablero"],
    faqs: [
      {
        question: "Puedo mover en curva?",
        answer: "No. La regla publica habla de movimiento en linea recta.",
      },
      {
        question: "Los dados siempre aparecen?",
        answer: "No. Depende del modo. Ciencia no usa dados visibles para decidir la jugada.",
      },
    ],
    internalLinks: [
      { label: "Modo Ciencia", path: route("ciencia") },
      { label: "Modo Azar", path: route("azar") },
      { label: "Reglas de XIMA", path: route("reglas") },
    ],
    cta: {
      label: "Comparar modos de juego",
      path: route("modos-de-juego"),
    },
    breadcrumbs: [
      { label: "Reglas", path: route("reglas") },
      { label: "Movimientos", path: route("reglas/movimientos") },
    ],
    sources: ["docs/xima-knowledge/sistema-de-movimientos.md"],
    status: "partial",
  }),
  page({
    slug: "modos-de-juego",
    friendlyUrl: canonical("modos-de-juego"),
    title: "Modos de juego de XIMA | Ciencia, Azar, Cienzar y FreeBox",
    metaDescription:
      "Conoce los modos de juego disponibles en XIMA: Ciencia, Azar, Cienzar, Ciencia V/S Azar, FreeBox, bot y amigos local.",
    h1: "Modos de juego de XIMA",
    intro:
      "XIMA ofrece varios modos para jugar, practicar y competir. Algunos usan decision libre, otros dados y otros combinan roles o reglas especiales.",
    sections: [
      {
        heading: "Modos principales",
        body:
          "La creacion de partidas incluye Ciencia, Azar, Cienzar y Ciencia V/S Azar. Cada modo conserva las bases de XIMA, pero cambia la forma en que se toman decisiones o se usan dados.",
      },
      {
        heading: "Practica y entrenamiento",
        body:
          "FreeBox funciona como caja de arena y jugar contra bot permite practicar contra inteligencia artificial con dificultad elegible.",
      },
      {
        heading: "Juego social",
        body:
          "El proyecto tambien permite jugar con amigos en el mismo dispositivo y organizar partidas con otros jugadores.",
      },
    ],
    examples: [
      "Elige Ciencia si quieres jugar sin dados visibles.",
      "Elige Azar si quieres que los dados formen parte del movimiento.",
    ],
    screenshots: ["Selector de modos en crear partida", "Tarjetas de modos en el lobby"],
    faqs: [
      {
        question: "Cual modo deberia probar primero?",
        answer: "Ciencia y Azar son los modos mas directos para entender la diferencia entre decision libre y dados.",
      },
      {
        question: "Cienzar ya esta disponible?",
        answer:
          "Si. Cienzar aparece como modo en el producto y se entiende como una modalidad hibrida relacionada con Ciencia y Azar.",
      },
    ],
    internalLinks: [
      { label: "Modo Ciencia", path: route("ciencia") },
      { label: "Modo Azar", path: route("azar") },
      { label: "FreeBox", path: route("modos-de-juego") },
    ],
    cta: {
      label: "Crear una partida",
      path: route("tutoriales/crear-partida"),
    },
    breadcrumbs: [{ label: "Modos de juego", path: route("modos-de-juego") }],
    sources: [
      "docs/xima-knowledge/modo-ciencia.md",
      "docs/xima-knowledge/modo-azar.md",
      "docs/xima-knowledge/modo-cienzar.md",
      "docs/xima-knowledge/freebox.md",
    ],
    status: "partial",
  }),
  page({
    slug: "ciencia",
    aliases: ["modos/ciencia"],
    friendlyUrl: canonical("ciencia"),
    title: "Modo Ciencia en XIMA | Jugar sin dados visibles",
    metaDescription:
      "Aprende que es el Modo Ciencia de XIMA, como aparece en la plataforma y que lo diferencia de los modos con dados.",
    h1: "Modo Ciencia",
    intro:
      "Modo Ciencia corresponde al modo `free` en frontend/backend y `Free` en Unity. En este modo no se muestran dados para decidir la jugada.",
    sections: [
      {
        heading: "Como funciona",
        body:
          "El jugador toma decisiones de movimiento sin depender de una tirada visible. Se mantienen las reglas base de tablero, bloques, movimiento, captura y rescate.",
      },
      {
        heading: "Donde aparece",
        body:
          "Aparece en crear partida, lobby, estadisticas y ranking por modo.",
      },
    ],
    examples: ["Puedes practicar lectura de tablero y rutas sin esperar resultado de dados."],
    screenshots: ["Tarjeta de Modo Ciencia", "Partida en modo Ciencia sin UI de dados"],
    faqs: [
      {
        question: "Modo Ciencia usa dados?",
        answer: "La UI de dados se oculta para este modo.",
      },
    ],
    internalLinks: [
      { label: "Modo Azar", path: route("azar") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
      { label: "Modos de juego", path: route("modos-de-juego") },
    ],
    cta: {
      label: "Ver Modo Azar",
      path: route("azar"),
    },
    breadcrumbs: [
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Ciencia", path: route("ciencia") },
    ],
    sources: ["docs/xima-knowledge/modo-ciencia.md"],
  }),
  page({
    slug: "azar",
    aliases: ["modos/azar"],
    friendlyUrl: canonical("azar"),
    title: "Modo Azar en XIMA | Como funcionan los dados",
    metaDescription:
      "Aprende que es el Modo Azar de XIMA, como se relaciona con dados y que debes saber antes de jugar.",
    h1: "Modo Azar",
    intro:
      "Modo Azar corresponde al modo `dice` en frontend/backend y `Dice` en Unity. En este modo los dados forman parte del movimiento.",
    sections: [
      {
        heading: "Dados y movimiento",
        body:
          "La interfaz y los scripts de Unity tienen soporte para tirar dados y usar el resultado en la jugada cuando el modo lo permite.",
      },
      {
        heading: "Reglas base",
        body:
          "Aunque el modo usa dados, se mantienen las reglas de bloques, tablero, altura, captura, rescate y victoria.",
      },
    ],
    examples: ["El resultado de dados puede limitar o guiar la cantidad de pasos de una jugada."],
    screenshots: ["UI de dados", "Selector de Modo Azar"],
    faqs: [
      {
        question: "Azar cambia el objetivo?",
        answer: "No. El objetivo sigue siendo llevar el bloque especial a la cima.",
      },
      {
        question: "Los dados aparecen en el replay?",
        answer: "El replay puede mostrar valores de dados cuando existen en la linea de tiempo.",
      },
    ],
    internalLinks: [
      { label: "Modo Ciencia", path: route("ciencia") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
      { label: "Historial y replay", path: route("historial-y-replay") },
    ],
    cta: {
      label: "Comparar modos",
      path: route("modos-de-juego"),
    },
    breadcrumbs: [
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Azar", path: route("azar") },
    ],
    sources: ["docs/xima-knowledge/modo-azar.md"],
    status: "partial",
  }),
  page({
    slug: "cienzar",
    friendlyUrl: canonical("cienzar"),
    title: "Modo Cienzar en XIMA | Ciencia y Azar en un modo hibrido",
    metaDescription:
      "Conoce el Modo Cienzar de XIMA, un modo verificado en el proyecto que combina elementos de Ciencia y Azar.",
    h1: "Modo Cienzar",
    intro:
      "Cienzar existe como modo dentro de XIMA. El codigo lo trata como una experiencia hibrida donde pueden intervenir flujo de decision libre y flujo de dados.",
    sections: [
      {
        heading: "Estado verificado",
        body:
          "Cienzar aparece como modo en crear partida, lobby, estadisticas y ranking. En Unity se mapea como `Sciluck`.",
      },
      {
        heading: "Relacion con Ciencia y Azar",
        body:
          "La documentacion interna indica que Cienzar combina elementos de Ciencia y Azar, manteniendo las reglas base del tablero, bloques, movimiento y victoria.",
      },
      {
        heading: "Vision del libro",
        body:
          "El libro presenta Cienzar como un genero hibrido entre juegos con azar y juegos de ciencia, pensado para equilibrar decision estrategica y resultado aleatorio.",
      },
      {
        heading: "Como interpretarlo",
        body:
          "Usa Cienzar como referencia de modo hibrido: conserva el objetivo de llegar a la cima y relaciona la lectura del tablero con elementos de azar cuando el flujo de partida los muestra.",
      },
    ],
    examples: [
      "Cienzar debe presentarse como modo hibrido, no como una regla nueva del tablero.",
      "Si una guia compara Ciencia, Azar y Cienzar, Cienzar se entiende mejor como puente entre decision y resultado aleatorio.",
      "La definicion del libro ayuda a explicar el nombre, pero no reemplaza el comportamiento implementado por frontend, backend y Unity.",
    ],
    screenshots: ["Selector de modo con Cienzar", "Ranking o estadisticas con Cienzar como modo"],
    faqs: [
      {
        question: "Cienzar existe en XIMA?",
        answer:
          "Si. Esta verificado en frontend, backend y Unity como modo propio.",
      },
      {
        question: "Como estudiar Cienzar?",
        answer:
          "Empieza comparando Ciencia y Azar, luego revisa como Cienzar aparece en crear partida, estadisticas y ranking.",
      },
    ],
    internalLinks: [
      { label: "Modo Ciencia", path: route("ciencia") },
      { label: "Modo Azar", path: route("azar") },
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Glosario de XIMA", path: route("glosario") },
      { label: "Cienzar como modo hibrido", path: route("modos/cienzar-ciencia-y-azar") },
      { label: "CIMAX segun el libro", path: route("cimax/segun-el-libro") },
    ],
    cta: {
      label: "Comparar modos",
      path: route("modos-de-juego"),
    },
    breadcrumbs: [
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Cienzar", path: route("cienzar") },
    ],
    sources: [
      "docs/xima-knowledge/modo-cienzar.md",
      "frontend/public/learn/library.json#como-llegar-a-la-xima",
    ],
    status: "ownerConfirmation",
    robots: "noindex,follow",
  }),
  page({
    slug: "partidas-online",
    friendlyUrl: canonical("partidas-online"),
    title: "Partidas online en XIMA | Crear, unirse y jugar",
    metaDescription:
      "Aprende como funciona el lobby de XIMA, como crear partidas, unirte a partidas publicas o privadas y volver a jugar.",
    h1: "Partidas online en XIMA",
    intro:
      "El lobby y los listados de XIMA conectan a los jugadores con modos, partidas disponibles, historial, amigos y herramientas de juego.",
    sections: [
      {
        heading: "Lobby",
        body:
          "El lobby es la pantalla central para acceder a modos, Aprender, historial, tienda, estadisticas, espectadores, amigos, puzzles, bot y caja de arena.",
      },
      {
        heading: "Crear partida",
        body:
          "Desde crear partida puedes elegir modo, jugadores, tiempo por turno y otros parametros disponibles en la interfaz.",
      },
      {
        heading: "Unirse a partida",
        body:
          "Los listados pueden mostrar partidas con modo, jugadores y tiempo. Si una partida tiene contrasena, debes introducirla para entrar.",
      },
    ],
    steps: [
      "Entra al lobby o al flujo de crear partida.",
      "Elige el modo de juego.",
      "Selecciona jugadores y tiempo si la pantalla lo permite.",
      "Crea la partida o elige una partida disponible.",
      "Espera a que la sala este lista para jugar.",
    ],
    screenshots: ["Lobby principal", "Listado de partidas disponibles"],
    faqs: [
      {
        question: "Donde creo una partida?",
        answer: "La ruta verificada es `/game/create-game` dentro del producto.",
      },
      {
        question: "Puedo jugar una partida privada?",
        answer: "Si. La UI permite activar contrasena de partido en el flujo correspondiente.",
      },
    ],
    internalLinks: [
      { label: "Crear una partida", path: route("tutoriales/crear-partida") },
      { label: "Unirse a partida privada", path: route("tutoriales/unirse-partida-privada") },
      { label: "Modos de juego", path: route("modos-de-juego") },
    ],
    cta: {
      label: "Aprender a crear partida",
      path: route("tutoriales/crear-partida"),
    },
    breadcrumbs: [{ label: "Partidas online", path: route("partidas-online") }],
    sources: [
      "docs/xima-knowledge/lobby.md",
      "docs/xima-knowledge/creacion-y-union-a-partidas.md",
      "docs/xima-knowledge/partidas-privadas-y-publicas.md",
    ],
    status: "partial",
  }),
  page({
    slug: "tutoriales/crear-partida",
    friendlyUrl: canonical("tutoriales/crear-partida"),
    title: "Como crear una partida en XIMA | Tutorial paso a paso",
    metaDescription:
      "Sigue los pasos para crear una partida en XIMA eligiendo modo, jugadores, tiempo y configuracion disponible.",
    h1: "Como crear una partida en XIMA",
    intro:
      "Crear una partida te permite elegir como quieres jugar antes de entrar a la sala.",
    sections: [
      {
        heading: "Modos disponibles",
        body:
          "La pantalla de crear partida ofrece modos Ciencia, Azar, Cienzar y Ciencia V/S Azar. Elige el modo antes de revisar jugadores, tiempo y configuracion de la sala.",
      },
      {
        heading: "Jugadores y tiempo",
        body:
          "El flujo muestra opciones de jugadores 1 vs 1, 1 vs 2 y 1 vs 3, ademas de tiempos visibles de 1, 2, 5 y 10 minutos.",
      },
      {
        heading: "Antes de crear",
        body:
          "Revisa el modo, la cantidad de jugadores y el tiempo antes de confirmar. Si quieres jugar contra bots, usa la pantalla separada de Jugar con bot.",
      },
    ],
    steps: [
      "Abre la opcion de crear partida.",
      "Selecciona el modo de juego.",
      "Elige la cantidad de jugadores.",
      "Selecciona el tiempo por turno si esta disponible.",
      "Revisa la configuracion.",
      "Crea la partida y espera a los jugadores.",
    ],
    examples: [
      "Elige Ciencia si quieres practicar decision libre segun las reglas verificadas.",
      "Elige Azar si quieres jugar con el flujo de dados visible en el proyecto.",
      "Usa Jugar con bot si lo que quieres es entrenar contra rivales controlados por el sistema.",
    ],
    screenshots: ["Pantalla Crear partida", "Selector de modo y jugadores"],
    faqs: [
      {
        question: "Que modo aparece por defecto?",
        answer: "El codigo de la pantalla usa Cienzar como valor inicial de creacion.",
      },
      {
        question: "Puedo crear una partida con bot desde aqui?",
        answer: "Existe una pantalla separada para jugar con bot.",
      },
    ],
    internalLinks: [
      { label: "Partidas online", path: route("partidas-online") },
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Unirse a partida privada", path: route("tutoriales/unirse-partida-privada") },
    ],
    cta: {
      label: "Ver modos de juego",
      path: route("modos-de-juego"),
    },
    breadcrumbs: [
      { label: "Tutoriales", path: route("partidas-online") },
      { label: "Crear partida", path: route("tutoriales/crear-partida") },
    ],
    sources: ["docs/xima-knowledge/creacion-y-union-a-partidas.md"],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "tutoriales/unirse-partida-privada",
    friendlyUrl: canonical("tutoriales/unirse-partida-privada"),
    title: "Como unirse a una partida privada en XIMA",
    metaDescription:
      "Aprende como entrar a una partida privada de XIMA usando la contrasena compartida por el organizador.",
    h1: "Como unirse a una partida privada",
    intro:
      "Las partidas privadas permiten limitar el acceso a quienes tengan la contrasena del partido.",
    sections: [
      {
        heading: "Que necesitas",
        body:
          "Necesitas el enlace o acceso a la partida y la contrasena compartida por quien la creo.",
      },
      {
        heading: "Ingreso seguro",
        body:
          "La contrasena debe compartirse solo con los participantes. La guia no publica detalles tecnicos de validacion ni almacenamiento.",
      },
      {
        heading: "Si no puedes entrar",
        body:
          "Comprueba que estas usando la contrasena correcta, que la partida sigue disponible y que el flujo de invitacion o listado corresponde a esa sala.",
      },
    ],
    steps: [
      "Recibe la contrasena de quien organizo la partida.",
      "Abre la partida desde el listado o invitacion.",
      "Cuando la UI la solicite, escribe la contrasena.",
      "Confirma y espera a entrar en la sala.",
    ],
    examples: [
      "Si el organizador cambia de partida, pide el nuevo acceso antes de intentar entrar.",
      "Si recibes la invitacion desde Amigos, revisa modo y tiempo antes de aceptarla.",
    ],
    screenshots: ["Indicador de partida con contrasena", "Formulario de ingreso de contrasena"],
    faqs: [
      {
        question: "Quien puede entrar a una partida privada?",
        answer: "Solo quienes tengan la contrasena correcta.",
      },
      {
        question: "Debo compartir la contrasena publicamente?",
        answer: "No. Compartela solo con los jugadores que quieres invitar.",
      },
    ],
    internalLinks: [
      { label: "Crear una partida", path: route("tutoriales/crear-partida") },
      { label: "Partidas online", path: route("partidas-online") },
      { label: "Amigos en XIMA", path: route("amigos") },
    ],
    cta: {
      label: "Crear partida ahora",
      path: route("tutoriales/crear-partida"),
    },
    breadcrumbs: [
      { label: "Tutoriales", path: route("partidas-online") },
      { label: "Unirse a partida privada", path: route("tutoriales/unirse-partida-privada") },
    ],
    sources: ["docs/xima-knowledge/partidas-privadas-y-publicas.md"],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "historial-y-replay",
    friendlyUrl: canonical("historial-y-replay"),
    title: "Historial y replay en XIMA | Revisa tus partidas",
    metaDescription:
      "Aprende como usar el historial y el replay de XIMA para revisar partidas, movimientos, timeline y velocidad de reproduccion.",
    h1: "Historial y replay",
    intro:
      "El historial guarda partidas anteriores y el replay permite reproducir partidas registradas para estudiar movimientos.",
    sections: [
      {
        heading: "Historial",
        body:
          "El historial muestra resultado, modo, jugadores, fecha, datos de rendimiento y acceso a replay cuando existe.",
      },
      {
        heading: "Replay",
        body:
          "El replay puede abrir una reproduccion con timeline, paso actual, controles de pausa/reproduccion, reinicio y velocidad.",
      },
      {
        heading: "Velocidad",
        body:
          "La UI contiene velocidades de 0.5x hasta 8x, ademas de controles lento/rapido y seguimiento de camara.",
      },
    ],
    steps: [
      "Abre Historial.",
      "Encuentra la partida que quieres revisar.",
      "Usa el boton de replay si esta disponible.",
      "Reproduce, pausa o cambia la velocidad.",
      "Usa el timeline para revisar jugadas concretas.",
    ],
    screenshots: ["Tabla de historial", "Panel de control de replay"],
    faqs: [
      {
        question: "Todas las partidas tienen replay?",
        answer: "La disponibilidad depende de que la partida tenga eventos de replay registrados.",
      },
      {
        question: "Puedo cambiar la velocidad?",
        answer: "Si. La interfaz incluye velocidades desde 0.5x hasta 8x.",
      },
    ],
    internalLinks: [
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
      { label: "Estadisticas", path: "/game/stats" },
      { label: "Reglas de XIMA", path: route("reglas") },
    ],
    cta: {
      label: "Ver reglas para estudiar mejor",
      path: route("reglas"),
    },
    breadcrumbs: [{ label: "Historial y replay", path: route("historial-y-replay") }],
    sources: [
      "docs/xima-knowledge/historial-de-partidas.md",
      "docs/xima-knowledge/reproduccion-de-partidas.md",
      "docs/xima-knowledge/controles-replay.md",
    ],
  }),
  page({
    slug: "sistema-de-anotacion",
    friendlyUrl: canonical("sistema-de-anotacion"),
    title: "Sistema de anotacion XIMA | Lee jugadas, replay y movimientos",
    metaDescription:
      "Aprende como funciona la anotacion de XIMA para registrar entradas, movimientos, replay y ejemplos de jugadas.",
    h1: "Sistema de anotacion",
    intro:
      "La anotacion de XIMA ayuda a leer jugadas, revisar replays y explicar movimientos sin depender solo de una captura.",
    sections: [
      {
        heading: "Donde aparece",
        body:
          "Unity genera notacion de jugadas y puede mostrarla en la interfaz. El replay tambien usa etiquetas, notacion y valores de dados cuando existen.",
      },
      {
        heading: "Entradas al tablero",
        body:
          "La documentacion interna indica que un bloque especial que entra desde fuera usa `=` seguido del destino, mientras que un bloque comun usa `-` seguido del destino.",
      },
      {
        heading: "Movimientos dentro del tablero",
        body:
          "Cuando un bloque ya esta en el tablero, la anotacion registra origen y destino para que la jugada pueda estudiarse despues.",
      },
      {
        heading: "Limite de publicacion",
        body:
          "La tabla completa de signos, abreviaturas y casos especiales debe confirmarse antes de publicar un manual avanzado.",
      },
      {
        heading: "Aporte del libro",
        body:
          "Como llegar a la Xima llama a este sistema notacion algebraica muy simplificada y diferencia notacion larga, notacion corta, signos de incorporacion y marcas para el bloque especial.",
      },
    ],
    steps: [
      "Abre una partida con replay disponible.",
      "Revisa la linea de tiempo de jugadas.",
      "Identifica si la jugada fue entrada al tablero o movimiento interno.",
      "Compara origen, destino y, si aplica, valor de dados.",
      "Usa la notacion junto con las reglas para estudiar la posicion.",
    ],
    examples: [
      "Una entrada de bloque especial puede registrarse con `=` y la casilla destino.",
      "Una entrada de bloque comun puede registrarse con `-` y la casilla destino.",
      "Un movimiento de bloque ya colocado debe permitir leer origen y destino.",
      "Cuando varias piezas pueden llegar a la misma casilla, la notacion avanzada debe desambiguar la jugada.",
    ],
    screenshots: [
      "Linea de tiempo del replay con jugadas",
      "Tabla de jugadas o anotacion dentro de una partida",
    ],
    faqs: [
      {
        question: "La anotacion reemplaza al replay?",
        answer:
          "No. La anotacion ayuda a leer movimientos; el replay permite reproducirlos visualmente.",
      },
      {
        question: "La tabla completa de anotacion ya esta cerrada?",
        answer:
          "No. Los signos basicos estan documentados, pero la tabla completa requiere confirmacion antes de ampliarse.",
      },
    ],
    internalLinks: [
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
      { label: "Tablero y casillas", path: route("reglas/tablero-y-casillas") },
      { label: "Glosario de XIMA", path: route("glosario") },
      { label: "Notacion algebraica", path: route("notacion/algebraica-muy-simplificada") },
      { label: "Signos de incorporacion", path: route("notacion/signos-incorporacion-especial") },
    ],
    cta: {
      label: "Estudiar historial y replay",
      path: route("historial-y-replay"),
    },
    breadcrumbs: [{ label: "Sistema de anotacion", path: route("sistema-de-anotacion") }],
    sources: [
      "docs/xima-knowledge/sistema-de-anotacion.md",
      "frontend/public/learn/library.json#como-llegar-a-la-xima",
    ],
    status: "partial",
  }),
  page({
    slug: "academia",
    friendlyUrl: canonical("academia"),
    title: "Academia XIMA | Rutas para aprender reglas, modos y replay",
    metaDescription:
      "Organiza tu aprendizaje de XIMA con rutas para principiantes, estudio de partidas, reglas, modos, puzzles y practica.",
    h1: "Academia XIMA",
    intro:
      "Academia XIMA es el centro recomendado para ordenar guias, tutoriales y practica sin duplicar las reglas oficiales.",
    sections: [
      {
        heading: "Ruta para principiantes",
        body:
          "El primer recorrido debe empezar por que es XIMA, objetivo, bloques, movimientos y como crear o unirse a una partida.",
      },
      {
        heading: "Ruta de estudio",
        body:
          "Para jugadores que ya jugaron, el aprendizaje debe conectar historial, replay, controles de velocidad y sistema de anotacion.",
      },
      {
        heading: "Ruta de practica",
        body:
          "La practica puede enlazar a modos, FreeBox, puzzles e inteligencia artificial cuando cada guia este validada desde la interfaz.",
      },
      {
        heading: "Contenido avanzado",
        body:
          "Aperturas, finales, tacticas avanzadas y analisis comentados deben publicarse solo con ejemplos confirmados.",
      },
    ],
    steps: [
      "Empieza por la guia Que es XIMA.",
      "Lee las reglas y el sistema de movimientos.",
      "Crea o juega una primera partida.",
      "Revisa el historial y el replay.",
      "Usa la anotacion para estudiar jugadas concretas.",
      "Avanza a modos, puzzles o IA cuando las guias esten disponibles.",
    ],
    examples: [
      "Un principiante puede seguir: Que es XIMA -> Reglas -> Como jugar -> Crear partida.",
      "Un jugador intermedio puede seguir: Historial y replay -> Sistema de anotacion -> Diccionario.",
      "Un jugador que practica puede alternar modos, FreeBox, puzzles e IA segun disponibilidad verificada.",
    ],
    screenshots: [
      "Hub Academia con rutas de aprendizaje",
      "Tarjetas de lecciones y guias relacionadas",
    ],
    faqs: [
      {
        question: "Academia XIMA es un curso cerrado?",
        answer:
          "No en esta fase. Es un hub que ordena contenidos verificados y deja pendientes los cursos avanzados.",
      },
      {
        question: "Se publicaran aperturas y finales?",
        answer:
          "Solo cuando existan ejemplos aprobados y revisados contra las reglas oficiales.",
      },
    ],
    internalLinks: [
      { label: "Que es XIMA", path: route("que-es-xima") },
      { label: "Como jugar XIMA", path: route("como-jugar") },
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Diccionario", path: route("diccionario") },
    ],
    cta: {
      label: "Empezar por Como jugar",
      path: route("como-jugar"),
    },
    breadcrumbs: [{ label: "Academia", path: route("academia") }],
    sources: ["docs/xima-knowledge/", "frontend/public/learn/library.json"],
    status: "partial",
    structuredDataType: "CollectionPage",
  }),
  page({
    slug: "diccionario",
    aliases: ["diccionario/estrategia"],
    friendlyUrl: canonical("diccionario"),
    title: "Diccionario XIMA | Conceptos de estrategia, reglas y replay",
    metaDescription:
      "Consulta conceptos clave de XIMA y estrategia: bloque especial, cima, movimiento, riesgo, tactica, replay y Cienzar.",
    h1: "Diccionario XIMA",
    intro:
      "El diccionario organiza terminos del juego y conceptos de estrategia para que cada guia use nombres consistentes.",
    sections: [
      {
        heading: "Terminos del juego",
        body:
          "Bloque especial, bloque comun, cima, incorporacion, captura, rescate y replay son terminos verificados en reglas, UI o documentos internos.",
      },
      {
        heading: "Conceptos de modos",
        body:
          "Ciencia y Azar tienen base verificable. Cienzar existe como modo, pero su definicion publica completa sigue pendiente de confirmacion.",
      },
      {
        heading: "Conceptos de estrategia",
        body:
          "Estrategia, tactica, riesgo, planificacion y analisis pueden explicarse de forma prudente, sin prometer beneficios no verificados.",
      },
      {
        heading: "URL por termino",
        body:
          "Las entradas individuales deben crearse en tandas pequenas, solo cuando tengan definicion, ejemplo y enlaces suficientes.",
      },
    ],
    examples: [
      "Replay se enlaza desde Historial y replay, controles y anotacion.",
      "Riesgo se enlaza desde Modo Azar y articulos de toma de decisiones.",
      "Cienzar se mantiene con nota de confirmacion hasta cerrar su definicion publica.",
    ],
    screenshots: ["Indice del diccionario", "Entrada de termino con enlaces internos"],
    faqs: [
      {
        question: "El diccionario reemplaza al glosario?",
        answer:
          "No. El glosario actual puede seguir como base; el diccionario organiza futuras entradas ampliadas.",
      },
      {
        question: "Todos los terminos tendran pagina propia?",
        answer:
          "No. Solo los terminos con suficiente contenido verificado deben tener URL individual.",
      },
    ],
    internalLinks: [
      { label: "Glosario de XIMA", path: route("glosario") },
      { label: "Reglas de XIMA", path: route("reglas") },
      { label: "Modo Azar", path: route("azar") },
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
    ],
    cta: {
      label: "Ver glosario actual",
      path: route("glosario"),
    },
    breadcrumbs: [{ label: "Diccionario", path: route("diccionario") }],
    sources: ["docs/xima-knowledge/glosario.md"],
    status: "partial",
    structuredDataType: "CollectionPage",
  }),
  page({
    slug: "glosario",
    friendlyUrl: canonical("glosario"),
    title: "Glosario de XIMA | Terminos clave del juego",
    metaDescription:
      "Consulta el glosario de XIMA con terminos como bloque especial, cima, dominacion, captura, rescate, Cienzar, FreeBox y replay.",
    h1: "Glosario de XIMA",
    intro:
      "Este glosario recoge terminos que aparecen en reglas, interfaz, modos de juego y documentos internos verificados.",
    sections: [
      {
        heading: "Bloque especial",
        body: "Bloque clave de cada jugador. Si llega a la cima en una jugada valida, gana.",
      },
      {
        heading: "Bloque comun",
        body:
          "Bloque ordinario que puede incorporarse, moverse, dominar, capturar y rescatar el especial capturado.",
      },
      {
        heading: "Dominacion",
        body:
          "Situacion donde un bloque sobre otro inmoviliza bloques inferiores mientras mantiene presion.",
      },
      {
        heading: "Captura",
        body:
          "Efecto de eliminar bloques de color minoritario en una columna bajo las condiciones indicadas por las reglas publicas.",
      },
      {
        heading: "Rescate",
        body:
          "Accion por la que un bloque comun puede llegar a la cima para recuperar un bloque especial capturado.",
      },
      {
        heading: "FreeBox",
        body:
          "Modo de practica o caja de arena con reglas especiales y movimiento libre mediante click derecho.",
      },
      {
        heading: "Cienzar y CIMAX",
        body:
          "Cienzar aparece como modo hibrido y CIMAX como metrica de progreso. Ambos ayudan a conectar reglas, modos, estadisticas y ranking.",
      },
    ],
    screenshots: ["Glosario dentro de Aprender"],
    faqs: [
      {
        question: "Que terminos conviene estudiar juntos?",
        answer: "Cienzar, Contrazar, CIMAX, XCIMA y G7 se entienden mejor al revisar modos, estadisticas, ranking y reglas de la cima.",
      },
    ],
    internalLinks: [
      { label: "Reglas de XIMA", path: route("reglas") },
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Bloques", path: route("reglas/bloques") },
    ],
    cta: {
      label: "Volver a reglas",
      path: route("reglas"),
    },
    breadcrumbs: [{ label: "Glosario", path: route("glosario") }],
    sources: ["docs/xima-knowledge/glosario.md"],
    status: "partial",
  }),
  page({
    slug: "soporte",
    friendlyUrl: canonical("soporte"),
    title: "Soporte de XIMA | Como pedir ayuda",
    metaDescription:
      "Aprende donde encontrar soporte en XIMA y que informacion incluir al reportar un problema de cuenta, partida, voz o tienda.",
    h1: "Soporte de XIMA",
    intro:
      "XIMA incluye soporte desde Ajustes y rutas internas de ayuda. La interfaz muestra un formulario para enviar email, asunto y descripcion del problema.",
    sections: [
      {
        heading: "Donde esta soporte",
        body:
          "La seccion de soporte aparece dentro de Ajustes y tambien existe una ruta de soporte en la experiencia de juego.",
      },
      {
        heading: "Que incluir",
        body:
          "Incluye una descripcion clara del problema, el contexto donde ocurrio y datos de contacto correctos. No compartas contrasenas ni informacion sensible.",
      },
      {
        heading: "Temas comunes",
        body:
          "Soporte puede relacionarse con cuenta, partidas, historial, replay, voz, movil, tienda o recompensas.",
      },
    ],
    steps: [
      "Abre Ajustes.",
      "Entra en la seccion de soporte.",
      "Escribe tu email de contacto.",
      "Agrega un asunto claro.",
      "Describe el problema sin incluir contrasenas.",
      "Envia el formulario.",
    ],
    screenshots: ["Formulario de soporte en Ajustes"],
    faqs: [
      {
        question: "Que correo aparece en la UI?",
        answer: "La pantalla de Ajustes muestra `info@juegoxima.com`.",
      },
      {
        question: "Debo enviar mi contrasena a soporte?",
        answer: "No. No compartas contrasenas ni claves en mensajes de soporte.",
      },
    ],
    internalLinks: [
      { label: "Partidas online", path: route("partidas-online") },
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Control por voz", path: "/game/settings?section=general" },
    ],
    cta: {
      label: "Volver a Aprender",
      path: route(),
    },
    breadcrumbs: [{ label: "Soporte", path: route("soporte") }],
    sources: ["docs/xima-knowledge/soporte.md"],
    status: "partial",
  }),
  page({
    slug: "reglas/tablero-y-casillas",
    aliases: ["tablero-y-casillas", "tablero"],
    friendlyUrl: canonical("reglas/tablero-y-casillas"),
    title: "Tablero y casillas de XIMA | Pisos, coordenadas y cima",
    metaDescription:
      "Aprende como se entiende el tablero de XIMA, que papel cumplen las casillas, los pisos y la cima dentro de una partida.",
    h1: "Tablero y casillas de XIMA",
    intro:
      "El tablero de XIMA es el espacio donde se incorporan y mueven los bloques. Comprender sus casillas, niveles y cima ayuda a leer mejor cada jugada.",
    sections: [
      {
        heading: "Tablero 3D",
        body:
          "La experiencia de juego se renderiza en Unity como un tablero tridimensional. Las casillas funcionan como destinos posibles para incorporar o mover bloques cuando la jugada es valida.",
      },
      {
        heading: "Casillas y lectura de posicion",
        body:
          "El sistema de anotacion y los replays usan referencias de posicion para describir movimientos. Por eso conviene aprender a mirar casillas, origen y destino como parte de una misma jugada.",
      },
      {
        heading: "La cima como objetivo",
        body:
          "La cima es el objetivo central del juego: el bloque especial propio debe llegar alli mediante una jugada valida. La pagina no agrega reglas nuevas; resume el comportamiento documentado en el proyecto.",
      },
      {
        heading: "Pisos, anillos y G7",
        body:
          "El libro describe el tablero como una piramide de cuatro pisos y usa ideas como anillos, bandas, caras y G7 para leer rutas de ascenso. Esa lectura avanzada complementa las coordenadas y no cambia las reglas basicas.",
      },
    ],
    examples: [
      "Al revisar un replay, la casilla de origen y la casilla destino ayudan a entender que bloque se movio.",
      "Cuando una guia menciona la cima, se refiere al punto de victoria verificado para el bloque especial.",
      "Si una guia habla de anillo interior, se refiere a una zona del piso que ayuda a estudiar rutas y defensas.",
    ],
    screenshots: ["Vista del tablero completo", "Casillas con coordenadas visibles", "Cima del tablero destacada"],
    faqs: [
      {
        question: "El tablero cambia el objetivo del juego?",
        answer: "No. El tablero organiza las posiciones; el objetivo verificado sigue siendo llevar el bloque especial a la cima.",
      },
      {
        question: "Las casillas aparecen en el replay?",
        answer: "El replay y la anotacion pueden mostrar referencias de movimiento que ayudan a ubicar origen y destino.",
      },
    ],
    internalLinks: [
      { label: "Reglas de XIMA", path: route("reglas") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
      { label: "Pisos, anillos y bandas", path: route("tablero/pisos-anillos-bandas") },
      { label: "G7 y simbolos de la cima", path: route("tablero/g7-y-simbolos-de-cima") },
    ],
    cta: {
      label: "Ver movimientos",
      path: route("reglas/movimientos"),
    },
    breadcrumbs: [
      { label: "Reglas", path: route("reglas") },
      { label: "Tablero y casillas", path: route("reglas/tablero-y-casillas") },
    ],
    sources: [
      "docs/xima-knowledge/tablero-y-casillas.md",
      "Xima_Game/Assets/Scripts/Core/Stage.cs",
      "Xima_Game/Assets/Scripts/Core/ScienceBot/ScienceBotMoveSelector.cs",
      "frontend/public/learn/library.json#como-llegar-a-la-xima",
    ],
  }),
  page({
    slug: "reglas/bloque-especial",
    aliases: ["bloque-especial"],
    friendlyUrl: canonical("reglas/bloque-especial"),
    title: "Bloque especial en XIMA | Objetivo, rescate y personalizacion",
    metaDescription:
      "Conoce el bloque especial de XIMA, su relacion con la cima, el rescate y la personalizacion mediante skins.",
    h1: "Bloque especial en XIMA",
    intro:
      "El bloque especial es la pieza clave de cada jugador: las reglas verificadas lo relacionan directamente con la condicion principal de victoria.",
    sections: [
      {
        heading: "Pieza principal del jugador",
        body:
          "Cada jugador cuenta con bloques comunes y un bloque especial. El bloque especial concentra el objetivo porque debe llegar a la cima para cerrar la partida a favor de su jugador.",
      },
      {
        heading: "Relacion con rescate y captura",
        body:
          "La documentacion interna menciona captura y rescate del bloque especial. Las explicaciones avanzadas de rescate deben mantenerse alineadas con las reglas oficiales antes de publicar ejemplos complejos.",
      },
      {
        heading: "Importancia estrategica",
        body:
          "El libro presenta el especial como una pieza defensiva en apertura y medio juego que puede volverse muy ofensiva en finales por su oposicion. Tambien recomienda incorporarlo con cuidado cuando exista una posicion ventajosa.",
      },
      {
        heading: "Personalizacion visual",
        body:
          "La interfaz de perfil y tienda permite seleccionar skins para el bloque especial cuando el jugador tiene acceso al recurso correspondiente.",
      },
    ],
    examples: [
      "Una guia de objetivo debe explicar siempre que la cima se alcanza con el bloque especial.",
      "Una guia de tienda puede enlazar aqui para aclarar que los skins cambian la apariencia, no las reglas publicas verificadas.",
      "En finales, la cercania entre especiales puede crear oposicion y decidir si hay apoyo suficiente para llegar a G7.",
    ],
    screenshots: ["Bloque especial en el tablero", "Selector de bloque especial en perfil", "Skin de bloque especial en tienda"],
    faqs: [
      {
        question: "El bloque especial es igual a los bloques comunes?",
        answer: "No. Ambos forman parte del juego, pero el bloque especial esta vinculado directamente con el objetivo de llegar a la cima.",
      },
      {
        question: "Un skin del bloque especial cambia las reglas?",
        answer: "El proyecto verifica personalizacion visual. No hay base para afirmar que un skin cambie las reglas.",
      },
    ],
    internalLinks: [
      { label: "Bloques comunes y especiales", path: route("reglas/bloques") },
      { label: "Objetivo y reglas", path: route("reglas") },
      { label: "Skins en XIMA", path: route("tienda/skins") },
      { label: "Oposicion del especial", path: route("reglas/oposicion-especial") },
      { label: "Amenazas al especial", path: route("reglas/amenazas-al-especial") },
    ],
    cta: {
      label: "Ver bloques",
      path: route("reglas/bloques"),
    },
    breadcrumbs: [
      { label: "Reglas", path: route("reglas") },
      { label: "Bloque especial", path: route("reglas/bloque-especial") },
    ],
    sources: [
      "docs/xima-knowledge/bloques-comunes-y-especiales.md",
      "frontend/src/pages/user/DesignMode.js",
      "Xima_Game/Assets/Scripts/Core/Cube.cs",
      "frontend/public/learn/library.json#como-llegar-a-la-xima",
    ],
    status: "partial",
  }),
  page({
    slug: "modos/ciencia-vs-azar",
    aliases: ["ciencia-vs-azar", "contrazar", "modos/contrazar"],
    friendlyUrl: canonical("modos/ciencia-vs-azar"),
    title: "Ciencia V/S Azar en XIMA | Modo mixto verificado",
    metaDescription:
      "Aprende que es Ciencia V/S Azar en XIMA, donde aparece y como se relaciona con Contrazar, estadisticas y ranking.",
    h1: "Ciencia V/S Azar",
    intro:
      "Ciencia V/S Azar aparece en los flujos de lobby, crear partida, bot, estadisticas y ranking. Esta pagina documenta lo verificable sin inventar reglas avanzadas.",
    sections: [
      {
        heading: "Donde aparece",
        body:
          "El modo se encuentra en interfaces de seleccion de modo y tambien se refleja en datos de progreso como estadisticas, ranking y CIMAX por modo.",
      },
      {
        heading: "Relacion con Contrazar",
        body:
          "El proyecto usa referencias a Ciencia V/S Azar y Contrazar. En Aprender se tratan como conceptos relacionados para ubicar el modo mixto en partidas, estadisticas y ranking.",
      },
      {
        heading: "Como usar esta informacion",
        body:
          "Usa esta guia para reconocer el modo, elegirlo en la interfaz cuando aparezca y comparar su lectura con Ciencia y Azar.",
      },
    ],
    examples: [
      "Si ves Ciencia V/S Azar en el selector de modos, tratalo como una modalidad propia dentro de XIMA.",
      "Si una estadistica muestra Contrazar, interpretala como dato relacionado con esta familia de modo mixto.",
    ],
    screenshots: ["Selector de modo con Ciencia V/S Azar", "Estadisticas con CIMAX Contrazar"],
    faqs: [
      {
        question: "Ciencia V/S Azar existe en XIMA?",
        answer: "Si. Esta verificado en la interfaz de juego, bot, estadisticas y ranking.",
      },
      {
        question: "Contrazar es lo mismo?",
        answer: "El proyecto relaciona ambos nombres dentro de modos y estadisticas. Esta guia los agrupa para que el jugador pueda reconocerlos sin confundirlos con Ciencia o Azar puros.",
      },
    ],
    internalLinks: [
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Modo Ciencia", path: route("ciencia") },
      { label: "Modo Azar", path: route("azar") },
      { label: "CIMAX", path: route("estadisticas/cimax") },
    ],
    cta: {
      label: "Comparar modos",
      path: route("modos-de-juego"),
    },
    breadcrumbs: [
      { label: "Modos", path: route("modos-de-juego") },
      { label: "Ciencia V/S Azar", path: route("modos/ciencia-vs-azar") },
    ],
    sources: [
      "docs/xima-knowledge/modo-ciencia-vs-azar.md",
      "frontend/src/pages/user/CreateGame.js",
      "frontend/src/pages/user/PlayBot.js",
      "frontend/src/pages/user/Stats.js",
      "frontend/src/pages/user/Ranking.js",
    ],
    status: "ownerConfirmation",
  }),
  page({
    slug: "modos/freebox",
    aliases: ["freebox"],
    friendlyUrl: canonical("modos/freebox"),
    title: "FreeBox en XIMA | Practica, explora y prueba movimientos",
    metaDescription:
      "Conoce FreeBox en XIMA, el modo de practica documentado para explorar movimientos y estudiar el tablero sin presentarlo como regla competitiva nueva.",
    h1: "FreeBox en XIMA",
    intro:
      "FreeBox aparece como una opcion de practica o caja de arena. Su valor principal es ayudar a explorar posiciones, movimientos y lectura del tablero.",
    sections: [
      {
        heading: "Modo de practica",
        body:
          "La documentacion interna describe FreeBox como una experiencia para practicar y probar reglas especiales o movimiento libre desde el entorno de juego.",
      },
      {
        heading: "Cuando usarlo",
        body:
          "FreeBox es util para revisar el tablero, observar como se comportan los bloques y preparar explicaciones sin depender de una partida competitiva.",
      },
      {
        heading: "No reemplaza las reglas oficiales",
        body:
          "Una practica en FreeBox no debe presentarse como regla de torneo. Las paginas de reglas deben seguir basandose en el comportamiento oficial verificado.",
      },
    ],
    examples: [
      "Un jugador nuevo puede usar FreeBox para reconocer casillas y alturas antes de entrar a una partida online.",
      "Un tutorial puede usar FreeBox para preparar una captura de pantalla de un movimiento.",
    ],
    screenshots: ["Acceso a FreeBox desde el lobby", "Tablero de practica en FreeBox"],
    faqs: [
      {
        question: "FreeBox es una partida competitiva?",
        answer: "La fuente disponible lo presenta como practica o caja de arena, no como sustituto de reglas competitivas.",
      },
      {
        question: "Puedo aprender movimientos con FreeBox?",
        answer: "Si. Es una forma razonable de observar el tablero y preparar ejercicios, manteniendo la diferencia con partidas oficiales.",
      },
    ],
    internalLinks: [
      { label: "Modos de juego", path: route("modos-de-juego") },
      { label: "Tablero y casillas", path: route("reglas/tablero-y-casillas") },
      { label: "Sistema de movimientos", path: route("reglas/movimientos") },
    ],
    cta: {
      label: "Ver movimientos",
      path: route("reglas/movimientos"),
    },
    breadcrumbs: [
      { label: "Modos", path: route("modos-de-juego") },
      { label: "FreeBox", path: route("modos/freebox") },
    ],
    sources: ["docs/xima-knowledge/freebox.md", "frontend/src/pages/user/Lobby.js", "Xima_Game/Assets/Scripts/UI/StartPanel.cs"],
    status: "partial",
  }),
  page({
    slug: "partidas/publicas-y-privadas",
    aliases: ["partidas-publicas-y-privadas"],
    friendlyUrl: canonical("partidas/publicas-y-privadas"),
    title: "Partidas publicas y privadas en XIMA | Diferencias y uso",
    metaDescription:
      "Aprende la diferencia entre partidas publicas y privadas en XIMA, como se relacionan con el lobby, contrasena e invitaciones.",
    h1: "Partidas publicas y privadas",
    intro:
      "XIMA permite explicar dos formas de acceso a una partida: partidas visibles para entrar desde flujos publicos y partidas privadas protegidas por invitacion o contrasena.",
    sections: [
      {
        heading: "Partidas publicas",
        body:
          "Las partidas publicas o disponibles se relacionan con el lobby y los listados donde los jugadores pueden buscar opciones activas para unirse.",
      },
      {
        heading: "Partidas privadas",
        body:
          "Las partidas privadas se explican desde el flujo de contrasena e invitaciones. La guia no expone detalles tecnicos de seguridad; solo explica la experiencia visible del usuario.",
      },
      {
        heading: "Elegir el tipo correcto",
        body:
          "Si quieres jugar con una persona especifica, usa el flujo privado o de amigos. Si quieres encontrar partidas abiertas, empieza por el lobby o buscar partidas.",
      },
    ],
    examples: [
      "Una partida con contrasena debe compartirse solo con quienes participaran.",
      "Una partida publica es mas apropiada cuando quieres encontrar rivales desde el lobby.",
    ],
    screenshots: ["Pantalla de crear partida", "Campo de contrasena de partida", "Listado de partidas disponibles"],
    faqs: [
      {
        question: "Debo publicar la contrasena de una partida privada?",
        answer: "No. Compártela solo con los jugadores que quieras invitar.",
      },
      {
        question: "Donde busco partidas abiertas?",
        answer: "El flujo visible para buscar partidas parte desde el lobby y las secciones de partidas disponibles.",
      },
    ],
    internalLinks: [
      { label: "Partidas online", path: route("partidas-online") },
      { label: "Crear una partida", path: route("tutoriales/crear-partida") },
      { label: "Unirse a partida privada", path: route("tutoriales/unirse-partida-privada") },
    ],
    cta: {
      label: "Crear una partida",
      path: route("tutoriales/crear-partida"),
    },
    breadcrumbs: [
      { label: "Partidas online", path: route("partidas-online") },
      { label: "Publicas y privadas", path: route("partidas/publicas-y-privadas") },
    ],
    sources: [
      "docs/xima-knowledge/partidas-privadas-y-publicas.md",
      "frontend/src/pages/user/CreateGame.js",
      "frontend/src/pages/user/Index.js",
      "backend/routes/tournament.js",
    ],
  }),
  page({
    slug: "lobby",
    friendlyUrl: canonical("lobby"),
    title: "Lobby de XIMA | Entrar a modos, partidas, noticias y aprendizaje",
    metaDescription:
      "Explora el lobby de XIMA: accesos a crear juego, modos, puzzles, bot, buscar partidas, noticias, amigos, tienda y Aprender.",
    h1: "Lobby de XIMA",
    intro:
      "El lobby es el punto de entrada de la experiencia de juego. Desde ahi se accede a modos, partidas, entrenamiento, comunidad y secciones de soporte o aprendizaje.",
    sections: [
      {
        heading: "Centro de navegacion",
        body:
          "El menu visible incluye Lobby, Aprender, Historial, Estadisticas, Ajustes, Tienda, Ver partidos, Amigos, Soporte, Noticias y Cerrar sesion.",
      },
      {
        heading: "Acciones principales",
        body:
          "La pantalla de lobby presenta opciones como crear juego, seleccionar modos, resolver puzzles, jugar con bot y buscar partidas, segun la configuracion visible del producto.",
      },
      {
        heading: "Alertas y accesos rapidos",
        body:
          "El layout muestra indicadores para novedades de amigos, soporte, tienda, noticias o Aprender. Estos avisos ayudan a volver a secciones con actividad pendiente.",
      },
    ],
    examples: [
      "Si quieres aprender reglas, entra en Aprender desde el menu.",
      "Si quieres practicar, busca opciones como bot, puzzle o FreeBox.",
      "Si quieres jugar online, usa crear juego o buscar partidas.",
    ],
    screenshots: ["Lobby de escritorio", "Menu inferior en movil", "Botones de modo dentro del lobby"],
    faqs: [
      {
        question: "El lobby es lo mismo que Aprender?",
        answer: "No. El lobby es el centro de navegacion del juego; Aprender es la seccion educativa.",
      },
      {
        question: "Puedo volver al lobby desde una guia?",
        answer: "Si. La interfaz de Aprender incluye acciones para volver al lobby o continuar con guias relacionadas.",
      },
    ],
    internalLinks: [
      { label: "Crear una partida", path: route("tutoriales/crear-partida") },
      { label: "Puzzles", path: route("puzzles") },
      { label: "Jugar con bot", path: route("tutoriales/jugar-con-bot") },
      { label: "Ajustes", path: route("ajustes") },
    ],
    cta: {
      label: "Crear una partida",
      path: route("tutoriales/crear-partida"),
    },
    breadcrumbs: [{ label: "Lobby", path: route("lobby") }],
    sources: ["docs/xima-knowledge/lobby.md", "frontend/src/layouts/GameLayout.js", "frontend/src/pages/user/Lobby.js"],
  }),
  page({
    slug: "replay/controles",
    aliases: ["controles-replay"],
    friendlyUrl: canonical("replay/controles"),
    title: "Controles de replay en XIMA | Reproducir, pausar y navegar",
    metaDescription:
      "Aprende que controles de replay existen en XIMA para revisar una partida: reproducir, pausar, reiniciar, velocidad, camara y timeline.",
    h1: "Controles de replay",
    intro:
      "Los controles de replay permiten revisar partidas finalizadas con mas calma. La interfaz muestra reproduccion, pausa, reinicio, velocidad, camara y lista de eventos.",
    sections: [
      {
        heading: "Panel de control",
        body:
          "El modal de replay incluye un contador de jugadas y un panel lateral con controles para avanzar por la partida cuando existe informacion de replay guardada.",
      },
      {
        heading: "Reproduccion y pausa",
        body:
          "El boton de reproducir o pausar cambia el estado del replay. Esta funcion ayuda a detenerse en una jugada especifica sin abandonar la revision.",
      },
      {
        heading: "Camara y tamano",
        body:
          "En escritorio, el panel puede ajustar tamano y vista; en movil se adapta a una experiencia de pantalla completa cuando corresponde.",
      },
    ],
    steps: [
      "Abre Historial o Ver partidos.",
      "Elige una partida que tenga replay disponible.",
      "Pulsa Ver replay o Repeticion.",
      "Usa reproducir, pausar, reiniciar o velocidad segun lo que quieras revisar.",
      "Consulta la timeline para saltar a jugadas concretas cuando este disponible.",
    ],
    examples: [
      "Pausa el replay justo antes de una captura para analizar el origen y destino.",
      "Reinicia el replay si quieres revisar toda la secuencia desde el inicio.",
    ],
    screenshots: ["Modal de replay", "Panel Control de replay", "Botones de play, pausa y reinicio"],
    faqs: [
      {
        question: "Todos los historiales tienen replay?",
        answer: "No necesariamente. La interfaz distingue partidas con replay disponible y partidas sin replay guardado.",
      },
      {
        question: "Puedo cambiar la velocidad del replay?",
        answer: "Si. El panel muestra controles de velocidad cuando el replay esta listo.",
      },
    ],
    internalLinks: [
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Velocidad de replay", path: route("replay/velocidad") },
      { label: "Timeline del replay", path: route("replay/timeline") },
    ],
    cta: {
      label: "Ver timeline",
      path: route("replay/timeline"),
    },
    breadcrumbs: [
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Controles", path: route("replay/controles") },
    ],
    sources: [
      "docs/xima-knowledge/controles-replay.md",
      "frontend/src/pages/user/Index.js",
      "frontend/src/pages/user/SpectateMatches.js",
      "backend/routes/replay.js",
    ],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "replay/velocidad",
    friendlyUrl: canonical("replay/velocidad"),
    title: "Velocidad de replay en XIMA | Revisar partidas a tu ritmo",
    metaDescription:
      "Aprende como funciona la velocidad de replay en XIMA y cuando usarla para estudiar jugadas, capturas y movimientos.",
    h1: "Velocidad de replay",
    intro:
      "La velocidad de replay permite acelerar o reducir el ritmo de una repeticion para estudiar la partida con mas control.",
    sections: [
      {
        heading: "Control de velocidad",
        body:
          "La UI del replay muestra la velocidad actual y botones para aumentarla o disminuirla cuando la reproduccion esta disponible.",
      },
      {
        heading: "Uso para aprendizaje",
        body:
          "Reducir la velocidad ayuda a revisar movimientos complejos. Aumentarla sirve para recorrer una partida larga hasta llegar a una posicion importante.",
      },
      {
        heading: "Relación con Unity",
        body:
          "El runtime de Unity resuelve un multiplicador de reproduccion para adaptar animaciones y dados durante la repeticion.",
      },
    ],
    examples: [
      "Usa menor velocidad para ver exactamente donde termina un bloque.",
      "Usa mayor velocidad para llegar rapido a las ultimas jugadas de una partida.",
    ],
    screenshots: ["Indicador de velocidad de replay", "Botones de disminuir y aumentar velocidad"],
    faqs: [
      {
        question: "La velocidad cambia el resultado del replay?",
        answer: "No. Solo cambia el ritmo de reproduccion para revisar la partida.",
      },
      {
        question: "La velocidad aparece siempre?",
        answer: "Aparece cuando el replay esta listo y tiene pasos suficientes para controlar la reproduccion.",
      },
    ],
    internalLinks: [
      { label: "Controles de replay", path: route("replay/controles") },
      { label: "Timeline del replay", path: route("replay/timeline") },
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
    ],
    cta: {
      label: "Ver controles",
      path: route("replay/controles"),
    },
    breadcrumbs: [
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Velocidad", path: route("replay/velocidad") },
    ],
    sources: [
      "docs/xima-knowledge/controles-replay.md",
      "frontend/src/pages/user/Index.js",
      "Xima_Game/Assets/Scripts/Core/OnlineMultiplayerBridge.cs",
      "Xima_Game/Assets/Scripts/Dice.cs",
    ],
  }),
  page({
    slug: "replay/timeline",
    friendlyUrl: canonical("replay/timeline"),
    title: "Timeline del replay en XIMA | Leer eventos y jugadas",
    metaDescription:
      "Aprende como usar la timeline del replay de XIMA para revisar pasos, eventos y movimientos de una partida finalizada.",
    h1: "Timeline del replay",
    intro:
      "La timeline del replay ordena los eventos de una partida para que puedas saltar mentalmente entre momentos importantes y entender la secuencia.",
    sections: [
      {
        heading: "Lista de eventos",
        body:
          "El panel lateral de replay muestra eventos cuando existen datos suficientes. Cada entrada se relaciona con un paso o jugada de la repeticion.",
      },
      {
        heading: "Relacion con anotacion",
        body:
          "La timeline complementa el sistema de anotacion porque muestra la secuencia que el jugador puede estudiar despues de la partida.",
      },
      {
        heading: "Uso recomendado",
        body:
          "Usala para localizar capturas, movimientos de dados, cambios de turno o jugadas que expliquen por que la partida termino de una forma determinada.",
      },
    ],
    examples: [
      "Si una partida tuvo una captura importante, revisa la timeline para ubicar el evento y volver a observarlo.",
      "Si un replay no muestra eventos, usa los controles generales o revisa si la partida tenia replay guardado.",
    ],
    screenshots: ["Timeline del replay", "Evento seleccionado dentro del panel", "Contador de pasos del replay"],
    faqs: [
      {
        question: "La timeline reemplaza el replay visual?",
        answer: "No. La timeline ayuda a navegar; el tablero visual sigue mostrando la posicion y los movimientos.",
      },
      {
        question: "Que pasa si no hay eventos?",
        answer: "La interfaz puede indicar que no hay eventos de replay cuando la partida no tiene datos suficientes.",
      },
    ],
    internalLinks: [
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
      { label: "Controles de replay", path: route("replay/controles") },
      { label: "Historial y replay", path: route("historial-y-replay") },
    ],
    cta: {
      label: "Aprender anotacion",
      path: route("sistema-de-anotacion"),
    },
    breadcrumbs: [
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Timeline", path: route("replay/timeline") },
    ],
    sources: ["frontend/src/pages/user/Index.js", "backend/models/MatchHistory.js", "backend/controllers/replayController.js"],
  }),
  page({
    slug: "estadisticas",
    friendlyUrl: canonical("estadisticas"),
    title: "Estadisticas de XIMA | Progreso, victorias y modos",
    metaDescription:
      "Consulta que estadisticas muestra XIMA: torneos ganados por modo, CIMAX, partidas, victorias, rachas y datos de perfil.",
    h1: "Estadisticas de XIMA",
    intro:
      "Las estadisticas ayudan a revisar el progreso del jugador. XIMA separa datos generales y datos por modo, incluyendo referencias a CIMAX.",
    sections: [
      {
        heading: "Resumen de progreso",
        body:
          "La interfaz de estadisticas presenta datos como torneos ganados, total de victorias, resultados por modo y enlaces hacia historial.",
      },
      {
        heading: "Estadisticas por modo",
        body:
          "El proyecto distingue Ciencia, Cienzar, Contrazar y Azar en estadisticas y ranking. Cienzar y Contrazar deben conservar notas de confirmacion cuando se expliquen en detalle.",
      },
      {
        heading: "Relacion con historial",
        body:
          "El backend de historial es una fuente principal para calcular o mostrar datos de partidas finalizadas, replay y metricas de jugador.",
      },
    ],
    examples: [
      "Un jugador puede usar estadisticas para comparar su avance en Ciencia y Azar.",
      "Desde estadisticas se puede orientar al jugador hacia Historial cuando necesita revisar partidas concretas.",
    ],
    screenshots: ["Pantalla Estadisticas", "Tarjetas de CIMAX por modo", "Acceso a Historial"],
    faqs: [
      {
        question: "Las estadisticas son publicas?",
        answer: "La seccion de estadisticas forma parte del area de usuario. Las guias solo explican los conceptos, no datos privados de jugadores.",
      },
      {
        question: "Que modos aparecen?",
        answer: "La UI referencia Ciencia, Cienzar, Contrazar y Azar en tarjetas de progreso.",
      },
    ],
    internalLinks: [
      { label: "CIMAX", path: route("estadisticas/cimax") },
      { label: "Ranking", path: route("ranking") },
      { label: "Historial y replay", path: route("historial-y-replay") },
    ],
    cta: {
      label: "Entender CIMAX",
      path: route("estadisticas/cimax"),
    },
    breadcrumbs: [{ label: "Estadisticas", path: route("estadisticas") }],
    sources: ["docs/xima-knowledge/estadisticas.md", "frontend/src/pages/user/Stats.js", "backend/utils/matchStats.js"],
    status: "partial",
  }),
  page({
    slug: "estadisticas/cimax",
    aliases: ["cimax"],
    friendlyUrl: canonical("estadisticas/cimax"),
    title: "CIMAX en XIMA | Puntuacion por modo y progreso",
    metaDescription:
      "Aprende que es CIMAX dentro de XIMA, donde aparece en la interfaz y como ayuda a leer progreso por modo.",
    h1: "CIMAX en XIMA",
    intro:
      "CIMAX aparece como puntuacion de progreso en estadisticas, ranking, lobby y sistemas de recompensas visuales. Esta guia explica lo verificable y enlaza la teoria del libro sin convertirla en calculadora publica.",
    sections: [
      {
        heading: "Donde aparece CIMAX",
        body:
          "La interfaz muestra CIMAX por modos como Ciencia, Cienzar, Contrazar y Azar. Tambien se usa para ranking y resumen de progreso.",
      },
      {
        heading: "Uso como senal de progreso",
        body:
          "CIMAX ayuda a presentar avance competitivo o historico del jugador. No debe confundirse con una regla de movimiento del tablero.",
      },
      {
        heading: "Marco del libro",
        body:
          "Como llegar a la Xima explica CIMAX con fuerza de juego, nivel de juego, potencia, tiempo efectivo, turnos y valores de bloques. El backend actual tambien registra fuerza, nivel, potencia y transferencia.",
      },
      {
        heading: "Como interpretarlo",
        body:
          "Lee CIMAX como una metrica de progreso dentro de XIMA: sirve para comparar rendimiento visible por modo, no para cambiar las reglas de movimiento del tablero.",
      },
    ],
    examples: [
      "Una tarjeta puede mostrar CIMAX Ciencia para resumir avance en ese modo.",
      "Un ranking puede ordenar o destacar jugadores usando datos asociados a modos.",
      "El libro permite explicar por que fuerza, tiempo y turnos importan al interpretar una partida, sin exponer datos privados.",
    ],
    screenshots: ["Tarjeta CIMAX en Estadisticas", "Ranking por modo con puntuacion", "Lobby con puntuaciones por modo"],
    faqs: [
      {
        question: "CIMAX cambia como se mueve un bloque?",
        answer: "No hay base para afirmarlo. CIMAX se documenta como progreso o puntuacion, no como regla de movimiento.",
      },
      {
        question: "CIMAX es una regla del tablero?",
        answer: "No. CIMAX pertenece a estadisticas, ranking y progreso; las reglas de movimiento se estudian en las guias de tablero y bloques.",
      },
    ],
    internalLinks: [
      { label: "Estadisticas", path: route("estadisticas") },
      { label: "Ranking", path: route("ranking") },
      { label: "Torneos", path: route("torneos") },
      { label: "CIMAX segun el libro", path: route("cimax/segun-el-libro") },
      { label: "Fuerza, nivel y potencia", path: route("cimax/fuerza-nivel-potencia") },
    ],
    cta: {
      label: "Ver CIMAX segun el libro",
      path: route("cimax/segun-el-libro"),
    },
    breadcrumbs: [
      { label: "Estadisticas", path: route("estadisticas") },
      { label: "CIMAX", path: route("estadisticas/cimax") },
    ],
    sources: [
      "frontend/src/pages/user/Stats.js",
      "frontend/src/pages/user/Ranking.js",
      "backend/utils/cimaxScoring.js",
      "Xima_Game/Assets/Scripts/Core/CimaxTracker.cs",
      "frontend/public/learn/library.json#como-llegar-a-la-xima",
    ],
    status: "partial",
  }),
  page({
    slug: "ranking",
    friendlyUrl: canonical("ranking"),
    title: "Ranking de XIMA | Clasificaciones globales y por modo",
    metaDescription:
      "Aprende como se presenta el ranking de XIMA, con clasificaciones por modo, presencia de jugadores y puntuaciones CIMAX.",
    h1: "Ranking de XIMA",
    intro:
      "El ranking muestra clasificaciones de jugadores usando datos por modo. Es una herramienta para comparar progreso competitivo sin exponer datos privados.",
    sections: [
      {
        heading: "Ranking por modo",
        body:
          "La pantalla de ranking organiza secciones por modo y muestra jugadores con puntuacion asociada. Los modos visibles incluyen Ciencia, Cienzar, Contrazar y Azar.",
      },
      {
        heading: "Ranking global",
        body:
          "El frontend construye una vista global combinando datos disponibles de los rankings por modo.",
      },
      {
        heading: "Actualizacion",
        body:
          "El ranking usa backend y socket para refrescar presencia o cambios relevantes, manteniendo la experiencia sincronizada cuando el usuario navega.",
      },
    ],
    examples: [
      "Si quieres comparar tu progreso en Azar, revisa la seccion del ranking de ese modo.",
      "Si una pagina habla de skins ganadas por mejores posiciones, debe enlazar al ranking y marcar reglas pendientes cuando corresponda.",
    ],
    screenshots: ["Pantalla Ranking", "Panel de ranking por modo", "Fila de jugador en ranking"],
    faqs: [
      {
        question: "El ranking muestra todos los datos de un jugador?",
        answer: "No. Las guias deben explicar el ranking de forma general y no publicar informacion privada de usuarios.",
      },
      {
        question: "Ranking y estadisticas son lo mismo?",
        answer: "No. Estadisticas resume progreso propio; ranking compara posiciones entre jugadores segun datos disponibles.",
      },
    ],
    internalLinks: [
      { label: "Estadisticas", path: route("estadisticas") },
      { label: "CIMAX", path: route("estadisticas/cimax") },
      { label: "Torneos", path: route("torneos") },
    ],
    cta: {
      label: "Ver estadisticas",
      path: route("estadisticas"),
    },
    breadcrumbs: [{ label: "Ranking", path: route("ranking") }],
    sources: ["docs/xima-knowledge/ranking.md", "frontend/src/pages/user/Ranking.js", "backend/controllers/tournament.js"],
    status: "partial",
  }),
  page({
    slug: "perfil",
    friendlyUrl: canonical("perfil"),
    title: "Perfil en XIMA | Datos visibles, skins y progreso",
    metaDescription:
      "Aprende que muestra el perfil de XIMA: codigo de jugador, avatar, estadisticas, historial reciente y personalizacion visual.",
    h1: "Perfil en XIMA",
    intro:
      "El perfil reune informacion visible del jugador, personalizacion y datos de progreso. Las guias deben explicar la funcion sin exponer informacion personal.",
    sections: [
      {
        heading: "Identidad de jugador",
        body:
          "El perfil puede mostrar nombre visible, codigo de jugador, avatar y estado. El codigo ayuda a buscar amigos o abrir perfiles desde la comunidad.",
      },
      {
        heading: "Progreso y partidas",
        body:
          "La vista de perfil de amigo incluye partidas, victorias, derrotas, tiempo medio, win rate, rachas, bloques capturados y replay cuando existe.",
      },
      {
        heading: "Personalizacion",
        body:
          "El modo de diseno permite seleccionar avatar, datos personales visibles y skins para bloque especial o bloques comunes segun acceso.",
      },
    ],
    examples: [
      "Comparte tu codigo de jugador si quieres que otro usuario te busque desde Amigos.",
      "Usa el perfil para revisar datos visibles antes de abrir un replay de una partida reciente.",
    ],
    screenshots: ["Perfil de amigo", "Editor de perfil", "Selector de avatar y skins"],
    faqs: [
      {
        question: "Debo publicar datos personales en el perfil?",
        answer: "No. La guia recomienda usar solo informacion necesaria y no compartir datos sensibles.",
      },
      {
        question: "El perfil enlaza con replay?",
        answer: "Si una partida tiene replay guardado, la vista de perfil puede ofrecer abrirlo desde el historial visible.",
      },
    ],
    internalLinks: [
      { label: "Amigos", path: route("amigos") },
      { label: "Estadisticas", path: route("estadisticas") },
      { label: "Skins", path: route("tienda/skins") },
    ],
    cta: {
      label: "Ver amigos",
      path: route("amigos"),
    },
    breadcrumbs: [{ label: "Perfil", path: route("perfil") }],
    sources: [
      "docs/xima-knowledge/perfil.md",
      "frontend/src/pages/user/FriendProfile.js",
      "frontend/src/pages/user/DesignMode.js",
      "backend/routes/friends.js",
    ],
  }),
  page({
    slug: "amigos",
    friendlyUrl: canonical("amigos"),
    title: "Amigos en XIMA | Buscar jugadores, solicitudes e invitaciones",
    metaDescription:
      "Aprende como funciona Amigos en XIMA: buscar por codigo, enviar solicitudes, aceptar invitaciones y unirte a partidas de amigos.",
    h1: "Amigos en XIMA",
    intro:
      "La seccion Amigos conecta jugadores mediante busqueda, solicitudes, listas de amigos e invitaciones a partida.",
    sections: [
      {
        heading: "Buscar por codigo",
        body:
          "La interfaz permite buscar jugadores usando un codigo de jugador. Ese flujo evita depender de informacion privada como contrasenas o datos personales.",
      },
      {
        heading: "Solicitudes de amistad",
        body:
          "El usuario puede enviar solicitudes, revisar solicitudes recibidas, aceptar, rechazar o cancelar solicitudes pendientes.",
      },
      {
        heading: "Invitaciones a partida",
        body:
          "Desde la lista de amigos se puede preparar una invitacion con modo y tiempo, o unirse a una partida de un amigo cuando el flujo lo permite.",
      },
    ],
    examples: [
      "Si un amigo te comparte su codigo, pegalo en el buscador de Amigos para enviar una solicitud.",
      "Si recibes una invitacion con modo y tiempo, revisa esos datos antes de aceptarla.",
    ],
    screenshots: ["Buscador de amigos", "Solicitudes recibidas", "Invitaciones de partida"],
    faqs: [
      {
        question: "Necesito el email de un jugador para agregarlo?",
        answer: "La interfaz visible prioriza el codigo de jugador para busqueda social.",
      },
      {
        question: "Puedo invitar a un amigo a una partida?",
        answer: "Si. La seccion Amigos incluye controles para enviar invitaciones con modo y tiempo.",
      },
    ],
    internalLinks: [
      { label: "Enviar solicitud de amistad", path: route("tutoriales/enviar-solicitud-amistad") },
      { label: "Invitar amigo a partida", path: route("tutoriales/invitar-amigo-a-partida") },
      { label: "Perfil", path: route("perfil") },
    ],
    cta: {
      label: "Enviar solicitud",
      path: route("tutoriales/enviar-solicitud-amistad"),
    },
    breadcrumbs: [{ label: "Amigos", path: route("amigos") }],
    sources: ["docs/xima-knowledge/amigos.md", "frontend/src/pages/user/Friends.js", "backend/routes/friends.js"],
  }),
  page({
    slug: "tutoriales/enviar-solicitud-amistad",
    friendlyUrl: canonical("tutoriales/enviar-solicitud-amistad"),
    title: "Como enviar una solicitud de amistad en XIMA",
    metaDescription:
      "Guia paso a paso para buscar un jugador por codigo y enviar una solicitud de amistad desde la seccion Amigos de XIMA.",
    h1: "Enviar solicitud de amistad",
    intro:
      "Para agregar un amigo en XIMA, usa el codigo de jugador dentro de la seccion Amigos y envia una solicitud desde el resultado encontrado.",
    sections: [
      {
        heading: "Dato necesario",
        body:
          "El flujo visible usa codigo de jugador. No necesitas pedir contrasenas ni datos sensibles para iniciar una solicitud.",
      },
      {
        heading: "Solicitud pendiente",
        body:
          "Despues de enviarla, la solicitud queda pendiente hasta que la otra persona la acepte o la rechace.",
      },
      {
        heading: "Buenas practicas",
        body:
          "Verifica que el codigo este bien escrito y evita enviar solicitudes repetidas si el sistema ya muestra una solicitud pendiente.",
      },
    ],
    steps: [
      "Abre Amigos desde el menu de XIMA.",
      "Busca el campo para ingresar codigo de jugador.",
      "Escribe o pega el codigo compartido por la otra persona.",
      "Revisa el resultado de busqueda.",
      "Pulsa Enviar solicitud de amistad.",
      "Espera a que la otra persona acepte la solicitud.",
    ],
    examples: [
      "Un codigo como XIMA-4K2 es el tipo de dato que la interfaz usa como ejemplo visual.",
      "Si no aparece ningun jugador, confirma que el codigo no tenga espacios o caracteres incorrectos.",
    ],
    screenshots: ["Campo de codigo de jugador", "Resultado de busqueda", "Boton Enviar solicitud de amistad"],
    faqs: [
      {
        question: "Que pasa si el codigo esta mal?",
        answer: "La busqueda puede no encontrar resultados. Revisa el codigo con la otra persona.",
      },
      {
        question: "Puedo cancelar una solicitud?",
        answer: "La interfaz de Amigos muestra invitaciones y solicitudes salientes, con controles para cancelar cuando aplica.",
      },
    ],
    internalLinks: [
      { label: "Amigos", path: route("amigos") },
      { label: "Perfil", path: route("perfil") },
      { label: "Invitar amigo a partida", path: route("tutoriales/invitar-amigo-a-partida") },
    ],
    cta: {
      label: "Ver invitaciones",
      path: route("tutoriales/invitar-amigo-a-partida"),
    },
    breadcrumbs: [
      { label: "Amigos", path: route("amigos") },
      { label: "Enviar solicitud", path: route("tutoriales/enviar-solicitud-amistad") },
    ],
    sources: ["frontend/src/pages/user/Friends.js", "backend/routes/friends.js"],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "tutoriales/invitar-amigo-a-partida",
    friendlyUrl: canonical("tutoriales/invitar-amigo-a-partida"),
    title: "Como invitar a un amigo a una partida de XIMA",
    metaDescription:
      "Aprende a invitar a un amigo a una partida de XIMA desde la lista de amigos, eligiendo modo y tiempo cuando la interfaz lo permite.",
    h1: "Invitar amigo a partida",
    intro:
      "La seccion Amigos permite enviar invitaciones de partida a jugadores agregados. La invitacion puede incluir datos como modo y tiempo.",
    sections: [
      {
        heading: "Invitacion desde la lista",
        body:
          "Cada amigo puede mostrar acciones como ver perfil, unirse o enviar invitacion. La disponibilidad depende del estado del jugador y del flujo activo.",
      },
      {
        heading: "Modo y tiempo",
        body:
          "La interfaz permite preparar una invitacion con modo de juego y tiempo por turno antes de enviarla.",
      },
      {
        heading: "Respuesta del amigo",
        body:
          "El amigo puede recibir, aceptar o rechazar la invitacion desde su propia seccion de Amigos.",
      },
    ],
    steps: [
      "Abre Amigos desde el menu.",
      "Busca el amigo dentro de tu lista.",
      "Elige el modo disponible para la invitacion.",
      "Selecciona el tiempo cuando el control aparezca.",
      "Pulsa el boton para enviar invitacion.",
      "Espera la respuesta del amigo.",
    ],
    examples: [
      "Puedes enviar una invitacion en un modo concreto si la opcion aparece en el selector.",
      "Si el amigo esta en partida, la interfaz puede ofrecer un estado o una accion distinta.",
    ],
    screenshots: ["Lista de amigos", "Selector de modo de invitacion", "Invitacion enviada"],
    faqs: [
      {
        question: "Puedo invitar a alguien que no es mi amigo?",
        answer: "El flujo documentado parte de la lista de amigos. Primero agrega al jugador si quieres usar este camino.",
      },
      {
        question: "La invitacion inicia la partida automaticamente?",
        answer: "No necesariamente. El otro jugador debe aceptar la invitacion o seguir el flujo que la interfaz indique.",
      },
    ],
    internalLinks: [
      { label: "Amigos", path: route("amigos") },
      { label: "Partidas privadas", path: route("partidas/publicas-y-privadas") },
      { label: "Unirse a partida privada", path: route("tutoriales/unirse-partida-privada") },
    ],
    cta: {
      label: "Ver partidas privadas",
      path: route("partidas/publicas-y-privadas"),
    },
    breadcrumbs: [
      { label: "Amigos", path: route("amigos") },
      { label: "Invitar amigo", path: route("tutoriales/invitar-amigo-a-partida") },
    ],
    sources: ["frontend/src/pages/user/Friends.js", "backend/routes/friends.js", "backend/models/FriendInvite.js"],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "ver-partidos",
    aliases: ["spectate", "partidos-en-vivo"],
    friendlyUrl: canonical("ver-partidos"),
    title: "Ver partidos en XIMA | Partidas en vivo y replays destacados",
    metaDescription:
      "Aprende como funciona Ver partidos en XIMA, con partidas en vivo, jugadores, etiquetas de modo y replays destacados.",
    h1: "Ver partidos en XIMA",
    intro:
      "La seccion Ver partidos permite explorar partidas en vivo y replays destacados cuando hay datos disponibles.",
    sections: [
      {
        heading: "Partidas en vivo",
        body:
          "La vista de espectador consulta partidas activas y muestra tarjetas con informacion como modo, jugadores y accion para mirar.",
      },
      {
        heading: "Replays destacados",
        body:
          "Ademas de partidas activas, la seccion puede mostrar replays destacados de partidas finalizadas cuando existe replay guardado.",
      },
      {
        heading: "Uso educativo",
        body:
          "Ver partidas ayuda a aprender sin entrar a jugar. El jugador puede observar decisiones, revisar modos y luego estudiar replays.",
      },
    ],
    examples: [
      "Si no quieres jugar todavia, abre Ver partidos para observar como se desarrollan partidas reales.",
      "Si ves un replay destacado, usalo junto con la timeline para estudiar una secuencia.",
    ],
    screenshots: ["Pantalla Ver partidos", "Tarjeta de partida en vivo", "Seccion de replays destacados"],
    faqs: [
      {
        question: "Siempre hay partidas en vivo?",
        answer: "No. La vista puede mostrar un estado vacio si no hay partidas disponibles.",
      },
      {
        question: "Puedo abrir un replay desde Ver partidos?",
        answer: "Si el historial destacado tiene replay guardado, la interfaz puede ofrecer Ver repeticion.",
      },
    ],
    internalLinks: [
      { label: "Replays destacados", path: route("replays-destacados") },
      { label: "Controles de replay", path: route("replay/controles") },
      { label: "Historial y replay", path: route("historial-y-replay") },
    ],
    cta: {
      label: "Ver replays destacados",
      path: route("replays-destacados"),
    },
    breadcrumbs: [{ label: "Ver partidos", path: route("ver-partidos") }],
    sources: ["frontend/src/pages/user/SpectateMatches.js", "backend/routes/tournament.js", "backend/controllers/tournament.js"],
  }),
  page({
    slug: "replays-destacados",
    friendlyUrl: canonical("replays-destacados"),
    title: "Replays destacados de XIMA | Estudia partidas guardadas",
    metaDescription:
      "Aprende como los replays destacados de XIMA ayudan a revisar partidas finalizadas, jugadores, modos y cantidad de pasos.",
    h1: "Replays destacados",
    intro:
      "Los replays destacados agrupan partidas finalizadas con repeticion disponible para que puedas observarlas sin depender de tu propio historial.",
    sections: [
      {
        heading: "Que muestra una tarjeta",
        body:
          "Las tarjetas pueden mostrar nombre de partida, modo, jugadores y cantidad de pasos de replay cuando esa informacion existe.",
      },
      {
        heading: "Cuando usar un replay destacado",
        body:
          "Son utiles para estudiar partidas ajenas, comparar modos o entender como se ve una repeticion antes de revisar tu propio historial.",
      },
      {
        heading: "Disponibilidad",
        body:
          "No todas las partidas tienen replay. La interfaz desactiva o evita abrir la repeticion cuando no hay datos guardados.",
      },
    ],
    examples: [
      "Un replay destacado con muchos pasos puede servir para practicar lectura de timeline.",
      "Una partida sin replay disponible no debe prometer reproduccion visual.",
    ],
    screenshots: ["Lista de replays destacados", "Boton Ver repeticion", "Datos de modo y jugadores"],
    faqs: [
      {
        question: "De donde salen los replays destacados?",
        answer: "La vista usa datos de historial y replay disponibles en el backend.",
      },
      {
        question: "Un replay destacado muestra datos privados?",
        answer: "Las guias deben explicar solo la interfaz visible y evitar exponer informacion personal de usuarios.",
      },
    ],
    internalLinks: [
      { label: "Ver partidos", path: route("ver-partidos") },
      { label: "Timeline del replay", path: route("replay/timeline") },
      { label: "Sistema de anotacion", path: route("sistema-de-anotacion") },
    ],
    cta: {
      label: "Aprender controles",
      path: route("replay/controles"),
    },
    breadcrumbs: [
      { label: "Ver partidos", path: route("ver-partidos") },
      { label: "Replays destacados", path: route("replays-destacados") },
    ],
    sources: ["frontend/src/pages/user/SpectateMatches.js", "backend/models/MatchHistory.js", "backend/controllers/replayController.js"],
  }),
  page({
    slug: "torneos",
    friendlyUrl: canonical("torneos"),
    title: "Torneos en XIMA | Sala de espera, posiciones y recompensas",
    metaDescription:
      "Aprende como se presentan los torneos en XIMA: informacion del torneo, sala de espera, chat, posiciones, costo y recompensa cuando aplica.",
    h1: "Torneos en XIMA",
    intro:
      "Los torneos son una forma organizada de competir dentro de XIMA. La interfaz muestra informacion de sala, jugadores, modo y controles para invitar o jugar.",
    sections: [
      {
        heading: "Informacion del torneo",
        body:
          "La sala de espera puede mostrar numero de torneo, modo, jugadores unidos, costo, recompensa y estado de acceso cuando esos datos existen.",
      },
      {
        heading: "Sala de espera",
        body:
          "Antes de jugar, los usuarios pueden revisar posiciones, chat de la sala, cuenta regresiva y acciones como invitar, salir o jugar.",
      },
      {
        heading: "Recompensas y costos",
        body:
          "El proyecto contempla valores de recompensa y costo en X. Cualquier explicacion comercial o legal debe seguir los terminos y confirmarse antes de hacer promesas.",
      },
    ],
    examples: [
      "Si la sala indica que requiere acceso, revisa la informacion antes de intentar entrar.",
      "Si hay chat de sala, usalo para coordinar sin compartir datos sensibles.",
    ],
    screenshots: ["Informacion del torneo", "Sala de espera de torneo", "Posiciones del torneo"],
    faqs: [
      {
        question: "Todos los torneos tienen recompensa?",
        answer: "No se debe asumir. La interfaz muestra recompensa solo cuando el registro del torneo la incluye.",
      },
      {
        question: "Puedo invitar a otros jugadores?",
        answer: "La sala de espera incluye una accion de invitar cuando hay URL o flujo disponible.",
      },
    ],
    internalLinks: [
      { label: "Unirse a torneo", path: route("tutoriales/unirse-a-torneo") },
      { label: "Ranking", path: route("ranking") },
      { label: "Recompensas", path: route("tienda/recompensas") },
    ],
    cta: {
      label: "Unirse a torneo",
      path: route("tutoriales/unirse-a-torneo"),
    },
    breadcrumbs: [{ label: "Torneos", path: route("torneos") }],
    sources: ["docs/xima-knowledge/torneos.md", "frontend/src/pages/user/Index.js", "backend/models/Tournament.js"],
    status: "partial",
  }),
  page({
    slug: "tutoriales/unirse-a-torneo",
    friendlyUrl: canonical("tutoriales/unirse-a-torneo"),
    title: "Como unirse a un torneo de XIMA | Guia paso a paso",
    metaDescription:
      "Sigue los pasos para unirte a un torneo en XIMA desde el lobby o la sala de espera, revisando modo, jugadores, costo y estado.",
    h1: "Unirse a un torneo",
    intro:
      "Antes de entrar a un torneo, revisa la informacion visible: modo, jugadores, costo, recompensa, acceso y cuenta regresiva cuando existan.",
    sections: [
      {
        heading: "Revisar la tarjeta",
        body:
          "Los listados de torneo pueden mostrar nombre, modo, estado y datos de participacion. No entres sin revisar si el torneo esta disponible.",
      },
      {
        heading: "Entrar a la sala",
        body:
          "Al unirte, la sala de espera muestra informacion del torneo, chat y posiciones. Desde alli puedes esperar o iniciar cuando la interfaz lo permita.",
      },
      {
        heading: "Cuidado con costos",
        body:
          "Si el torneo muestra costo en X, revisa tu saldo y las condiciones visibles. No se deben prometer recompensas fuera de lo que indique el producto.",
      },
    ],
    steps: [
      "Abre el lobby o la seccion donde aparecen torneos.",
      "Selecciona un torneo disponible.",
      "Revisa modo, jugadores, costo y recompensa si se muestran.",
      "Pulsa la accion para unirte o entrar.",
      "Espera en la sala hasta que puedas jugar.",
      "Usa Salir de la sala si decides no participar.",
    ],
    examples: [
      "Un torneo abierto puede permitir entrar sin contrasena.",
      "Un torneo con costo debe revisarse antes de confirmar la entrada.",
    ],
    screenshots: ["Listado de torneos", "Informacion del torneo", "Boton Jugar en sala de espera"],
    faqs: [
      {
        question: "Que pasa si el torneo esta lleno?",
        answer: "La interfaz de sala muestra jugadores unidos frente al tamano esperado. Si no hay cupo, sigue el mensaje visible.",
      },
      {
        question: "Puedo salir antes de jugar?",
        answer: "La sala de espera muestra una accion Salir de la sala cuando el flujo lo permite.",
      },
    ],
    internalLinks: [
      { label: "Torneos", path: route("torneos") },
      { label: "Ranking", path: route("ranking") },
      { label: "Solucion de problemas", path: route("solucion-de-problemas") },
    ],
    cta: {
      label: "Ver torneos",
      path: route("torneos"),
    },
    breadcrumbs: [
      { label: "Torneos", path: route("torneos") },
      { label: "Unirse", path: route("tutoriales/unirse-a-torneo") },
    ],
    sources: ["frontend/src/pages/user/Index.js", "backend/routes/tournament.js", "backend/models/Tournament.js"],
    structuredDataType: "HowTo",
    status: "partial",
  }),
  page({
    slug: "puzzles",
    friendlyUrl: canonical("puzzles"),
    title: "Puzzles de XIMA | Entrenamiento, modos y recompensas",
    metaDescription:
      "Aprende como se presentan los puzzles de XIMA para practicar posiciones, resolver desafios y revisar recompensas cuando existan.",
    h1: "Puzzles de XIMA",
    intro:
      "Los puzzles son una via de entrenamiento dentro de XIMA. Permiten abrir desafios, resolver posiciones y seguir progreso sin explicar herramientas internas de creacion.",
    sections: [
      {
        heading: "Entrenamiento por posicion",
        body:
          "La interfaz de puzzles se relaciona con posiciones guardadas y objetivos de solucion. Esta pagina documenta el flujo de jugador, no el flujo interno de creacion.",
      },
      {
        heading: "Modos y filtros",
        body:
          "El sistema puede organizar puzzles por modo y mostrar datos de solucion, recompensas o estadisticas agregadas dentro del dashboard de puzzles.",
      },
      {
        heading: "Recompensas",
        body:
          "El backend contempla reclamos de recompensa de puzzle. La guia debe hablar de forma general y marcar que valores o condiciones necesitan confirmacion oficial.",
      },
    ],
    examples: [
      "Un puzzle puede servir para practicar una secuencia sin abrir una partida online completa.",
      "Si un puzzle muestra recompensa, revisa su estado antes de asumir que se puede reclamar.",
    ],
    screenshots: ["Entrada de puzzles en lobby", "Dashboard de puzzles", "Puzzle abierto en el tablero"],
    faqs: [
      {
        question: "Esta pagina explica como crear puzzles?",
        answer: "No. La creacion y edicion interna de puzzles esta excluida del contenido publico.",
      },
      {
        question: "Todos los puzzles tienen recompensa?",
        answer: "No se debe asumir. La recompensa depende de los datos visibles del puzzle y requiere confirmacion oficial para valores.",
      },
    ],
    internalLinks: [
      { label: "Resolver puzzle", path: route("tutoriales/resolver-puzzle") },
      { label: "FreeBox", path: route("modos/freebox") },
      { label: "Recompensas", path: route("tienda/recompensas") },
    ],
    cta: {
      label: "Resolver puzzle",
      path: route("tutoriales/resolver-puzzle"),
    },
    breadcrumbs: [{ label: "Puzzles", path: route("puzzles") }],
    sources: ["docs/xima-knowledge/puzzles.md", "frontend/src/pages/user/Index.js", "backend/routes/tournament.js"],
    status: "partial",
  }),
  page({
    slug: "tutoriales/resolver-puzzle",
    friendlyUrl: canonical("tutoriales/resolver-puzzle"),
    title: "Como resolver un puzzle en XIMA | Tutorial de entrenamiento",
    metaDescription:
      "Aprende el flujo general para abrir y resolver un puzzle en XIMA, revisar el modo, jugar la posicion y consultar el resultado.",
    h1: "Resolver un puzzle",
    intro:
      "Resolver un puzzle consiste en abrir un desafio disponible, estudiar la posicion y completar el objetivo visible dentro del flujo de jugador.",
    sections: [
      {
        heading: "Abrir el desafio",
        body:
          "El lobby y la seccion de puzzles pueden mostrar entradas disponibles. El usuario debe seleccionar una entrada publica o visible para iniciar el flujo.",
      },
      {
        heading: "Jugar la posicion",
        body:
          "El tablero carga una posicion o estado de puzzle. La guia no inventa solucion; cada puzzle debe resolverse segun su posicion y reglas visibles.",
      },
      {
        heading: "Resultado",
        body:
          "El sistema diferencia estados como resuelto, fallido o recompensa reclamada cuando esos datos existen en el backend y la interfaz.",
      },
    ],
    steps: [
      "Abre el lobby.",
      "Entra a la seccion de puzzles.",
      "Elige un puzzle disponible.",
      "Revisa el modo o datos visibles del desafio.",
      "Juega la posicion en el tablero.",
      "Consulta el resultado y la recompensa solo si la interfaz la muestra.",
    ],
    examples: [
      "Si el puzzle pertenece a Azar, no debes explicar una solucion como si fuera Ciencia.",
      "Si el puzzle no carga, usa soporte o la pagina de solucion de problemas.",
    ],
    screenshots: ["Tarjeta de puzzle", "Puzzle cargado en tablero", "Estado de puzzle resuelto"],
    faqs: [
      {
        question: "Puedo ver la solucion antes de jugar?",
        answer: "No hay una fuente verificada para prometer soluciones previas. La guia se centra en el flujo visible.",
      },
      {
        question: "Que pasa si falla la carga?",
        answer: "El backend tiene rutas de reporte de fallo de carga; para usuarios se debe explicar como reportar el problema sin detalles internos.",
      },
    ],
    internalLinks: [
      { label: "Puzzles", path: route("puzzles") },
      { label: "Solucion de problemas", path: route("solucion-de-problemas") },
      { label: "Soporte", path: route("soporte") },
    ],
    cta: {
      label: "Ver puzzles",
      path: route("puzzles"),
    },
    breadcrumbs: [
      { label: "Puzzles", path: route("puzzles") },
      { label: "Resolver puzzle", path: route("tutoriales/resolver-puzzle") },
    ],
    sources: ["frontend/src/pages/user/Index.js", "backend/routes/tournament.js", "backend/controllers/tournament.js"],
    structuredDataType: "HowTo",
    status: "partial",
  }),
  page({
    slug: "bots",
    aliases: ["inteligencia-artificial"],
    friendlyUrl: canonical("bots"),
    title: "Bots de XIMA | Entrenamiento con IA y dificultad",
    metaDescription:
      "Conoce los bots de XIMA para entrenar, elegir modo, ajustar dificultad y practicar antes de jugar contra otros usuarios.",
    h1: "Bots de XIMA",
    intro:
      "XIMA incluye partidas con bot para entrenar. La interfaz permite escoger modo, cantidad de rivales, tiempo por turno y dificultad.",
    sections: [
      {
        heading: "Entrenar antes de competir",
        body:
          "La pantalla Jugar con bot presenta la experiencia como entrenamiento para llegar preparado a partidas contra otros jugadores.",
      },
      {
        heading: "Modos disponibles",
        body:
          "El flujo de bot incluye opciones relacionadas con modo libre, dados, Cienzar y Ciencia V/S Azar. Cienzar conserva notas de confirmacion para explicaciones avanzadas.",
      },
      {
        heading: "Dificultad",
        body:
          "El bot usa niveles de dificultad visibles en la interfaz. El codigo de IA incluye tiempos y limites internos, pero el contenido publico no debe convertirlos en promesas tacticas.",
      },
    ],
    examples: [
      "Un principiante puede empezar contra un bot antes de jugar una partida online.",
      "Un jugador intermedio puede practicar un modo especifico y luego revisar el replay si existe.",
    ],
    screenshots: ["Pantalla Jugar con bot", "Selector de dificultad", "Selector de modo con bot"],
    faqs: [
      {
        question: "El bot juega por mi?",
        answer: "No. La pantalla de bot crea una partida de entrenamiento contra rivales controlados por el sistema.",
      },
      {
        question: "Se puede explicar toda la logica de IA?",
        answer: "No es recomendable. La guia publica debe enfocarse en uso, modos y dificultad visibles.",
      },
    ],
    internalLinks: [
      { label: "Jugar con bot", path: route("tutoriales/jugar-con-bot") },
      { label: "Modo Azar", path: route("azar") },
      { label: "Ciencia V/S Azar", path: route("modos/ciencia-vs-azar") },
    ],
    cta: {
      label: "Jugar con bot",
      path: route("tutoriales/jugar-con-bot"),
    },
    breadcrumbs: [{ label: "Bots", path: route("bots") }],
    sources: [
      "docs/xima-knowledge/inteligencia-artificial.md",
      "frontend/src/pages/user/PlayBot.js",
      "backend/routes/scienceBot.js",
      "Xima_Game/Assets/Scripts/Core/ScienceBot/ScienceBotMoveSelector.cs",
    ],
    status: "partial",
  }),
  page({
    slug: "tutoriales/jugar-con-bot",
    friendlyUrl: canonical("tutoriales/jugar-con-bot"),
    title: "Como jugar con bot en XIMA | Practica con dificultad y modos",
    metaDescription:
      "Sigue los pasos para iniciar una partida con bot en XIMA, elegir modo, cantidad de rivales, tiempo y dificultad.",
    h1: "Jugar con bot",
    intro:
      "Jugar con bot es una forma de practicar XIMA desde una pantalla dedicada que permite configurar modo, rivales, tiempo y dificultad.",
    sections: [
      {
        heading: "Configurar la partida",
        body:
          "El flujo de bot guarda preferencias y crea una partida usando la ruta de torneos del backend, con datos de modo y dificultad.",
      },
      {
        heading: "Elegir dificultad",
        body:
          "La interfaz presenta dificultad como una decision del jugador. No se deben publicar detalles internos de evaluacion como estrategia garantizada.",
      },
      {
        heading: "Despues de jugar",
        body:
          "Cuando termines, revisa historial, estadisticas o replay si la partida genero datos disponibles.",
      },
    ],
    steps: [
      "Abre el lobby de XIMA.",
      "Elige Jugar con bot.",
      "Selecciona el modo de juego disponible.",
      "Elige cantidad de rivales bot.",
      "Selecciona tiempo por turno y dificultad.",
      "Pulsa Jugar para abrir la partida.",
    ],
    examples: [
      "Usa una dificultad baja si estas aprendiendo reglas basicas.",
      "Prueba Azar con bot si quieres practicar flujo con dados.",
    ],
    screenshots: ["Selector de modo con bot", "Selector de dificultad", "Boton Jugar"],
    faqs: [
      {
        question: "Puedo elegir mas de un bot?",
        answer: "La interfaz muestra opciones de cantidad de rivales bot, como uno, dos o tres, segun el flujo disponible.",
      },
      {
        question: "El bot tiene los mismos modos que una partida normal?",
        answer: "El flujo de bot tiene sus propias opciones de modo. Revisa el selector visible antes de iniciar.",
      },
    ],
    internalLinks: [
      { label: "Bots de XIMA", path: route("bots") },
      { label: "Historial y replay", path: route("historial-y-replay") },
      { label: "Estadisticas", path: route("estadisticas") },
    ],
    cta: {
      label: "Ver bots",
      path: route("bots"),
    },
    breadcrumbs: [
      { label: "Bots", path: route("bots") },
      { label: "Jugar con bot", path: route("tutoriales/jugar-con-bot") },
    ],
    sources: ["frontend/src/pages/user/PlayBot.js", "backend/routes/tournament.js", "backend/routes/scienceBot.js"],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "tienda",
    friendlyUrl: canonical("tienda"),
    title: "Tienda XIMA | Skins, bonos y recompensas promocionales",
    metaDescription:
      "Aprende como se organiza la tienda de XIMA: skins, bonos X, juego fisico, gift cards y recompensas promocionales verificadas.",
    h1: "Tienda XIMA",
    intro:
      "La tienda de XIMA permite personalizar la experiencia y revisar productos o recompensas promocionales visibles en la interfaz.",
    sections: [
      {
        heading: "Skins y personalizacion",
        body:
          "La tienda incluye productos de skins para bloque especial y bloques comunes. El perfil usa esos accesos para aplicar personalizacion visual.",
      },
      {
        heading: "Bonos y wallet",
        body:
          "La UI muestra saldo en X o bonos. Los terminos indican que los elementos virtuales se usan dentro del ecosistema XIMA y no deben presentarse como dinero real.",
      },
      {
        heading: "Recompensas promocionales",
        body:
          "El proyecto contempla productos fisicos, gift cards y reclamos promocionales. Cualquier detalle comercial debe confirmarse antes de publicarse como oferta oficial.",
      },
    ],
    examples: [
      "Compra o desbloquea un skin solo si la tienda muestra que esta disponible para tu cuenta.",
      "Si una recompensa requiere datos de envio, completa solo informacion necesaria y revisa la advertencia visible.",
    ],
    screenshots: ["Pantalla Tienda XIMA", "Saldo de bonos", "Producto de skin"],
    faqs: [
      {
        question: "Los bonos tienen valor fuera de XIMA?",
        answer: "Los terminos del proyecto indican que los elementos virtuales pertenecen al ecosistema XIMA y no se deben presentar como dinero real.",
      },
      {
        question: "La tienda cambia las reglas del juego?",
        answer: "No hay base para decirlo. La tienda se relaciona con personalizacion y recompensas visibles.",
      },
    ],
    internalLinks: [
      { label: "Skins", path: route("tienda/skins") },
      { label: "Recompensas", path: route("tienda/recompensas") },
      { label: "Perfil", path: route("perfil") },
    ],
    cta: {
      label: "Ver skins",
      path: route("tienda/skins"),
    },
    breadcrumbs: [{ label: "Tienda", path: route("tienda") }],
    sources: [
      "docs/xima-knowledge/tienda-y-recompensas.md",
      "frontend/src/pages/user/Store.js",
      "frontend/src/constants/termsAndConditions.js",
      "backend/routes/orders.js",
    ],
    status: "partial",
  }),
  page({
    slug: "tienda/skins",
    aliases: ["skins"],
    friendlyUrl: canonical("tienda/skins"),
    title: "Skins de XIMA | Bloque especial, bloques comunes y perfil",
    metaDescription:
      "Aprende como funcionan los skins de XIMA para personalizar el bloque especial y los bloques comunes desde tienda y perfil.",
    h1: "Skins de XIMA",
    intro:
      "Los skins son elementos visuales de personalizacion. XIMA los organiza por bloque especial, bloques comunes, categorias gratis, de pago o ganadas.",
    sections: [
      {
        heading: "Bloque especial y bloques comunes",
        body:
          "El modo de diseno permite elegir apariencia para el bloque especial y para bloques comunes usando recursos disponibles para el perfil.",
      },
      {
        heading: "Acceso a skins",
        body:
          "El proyecto distingue skins gratis, de pago y ganadas. Algunos skins pueden depender de compra o estado competitivo verificado.",
      },
      {
        heading: "Sin ventaja confirmada",
        body:
          "No hay fuente para afirmar que un skin cambie las reglas. Deben presentarse como personalizacion visual salvo confirmacion oficial contraria.",
      },
    ],
    examples: [
      "Elige un skin gratis desde perfil si esta disponible en tu cuenta.",
      "Si un skin indica requisito de top por modo, no prometas desbloqueo hasta confirmar las reglas oficiales.",
    ],
    screenshots: ["Catalogo de skins en tienda", "Selector de bloque especial", "Selector de bloques comunes"],
    faqs: [
      {
        question: "Que tipos de skins aparecen?",
        answer: "La UI distingue grupos como Gratis, de pago y Ganadas.",
      },
      {
        question: "Un skin puede estar bloqueado?",
        answer: "Si. El perfil y la tienda muestran estados de acceso segun propiedad o requisito.",
      },
    ],
    internalLinks: [
      { label: "Tienda", path: route("tienda") },
      { label: "Bloque especial", path: route("reglas/bloque-especial") },
      { label: "Perfil", path: route("perfil") },
    ],
    cta: {
      label: "Ver tienda",
      path: route("tienda"),
    },
    breadcrumbs: [
      { label: "Tienda", path: route("tienda") },
      { label: "Skins", path: route("tienda/skins") },
    ],
    sources: [
      "frontend/src/pages/user/Store.js",
      "frontend/src/pages/user/DesignMode.js",
      "frontend/src/constants/skinStoreCatalog.js",
      "Xima_Game/Assets/Scripts/UI/SpecialCubeSkinSelectionUI.cs",
    ],
    status: "partial",
  }),
  page({
    slug: "tienda/recompensas",
    aliases: ["recompensas"],
    friendlyUrl: canonical("tienda/recompensas"),
    title: "Recompensas en XIMA | Promociones, bonos y reclamos",
    metaDescription:
      "Conoce que se verifica sobre recompensas en XIMA: productos promocionales, gift cards, bonos y reclamos visibles en la tienda.",
    h1: "Recompensas en XIMA",
    intro:
      "XIMA contempla recompensas promocionales dentro de su ecosistema. Esta guia explica el flujo visible sin convertirlo en promesa comercial.",
    sections: [
      {
        heading: "Base verificada",
        body:
          "La tienda y el backend contienen flujos para productos fisicos, gift cards, disponibilidad diaria y reclamos con datos de contacto o envio.",
      },
      {
        heading: "Marco de uso",
        body:
          "Los terminos indican que las recompensas son promocionales y de entretenimiento. No deben presentarse como apuestas, dinero real o garantia de obtencion.",
      },
      {
        heading: "Disponibilidad y condiciones",
        body:
          "La disponibilidad de recompensas depende de lo que muestre la tienda en cada momento, incluyendo stock, limites visibles y condiciones del flujo de reclamo.",
      },
    ],
    examples: [
      "Si la tienda muestra una gift card agotada, la guia debe indicar que la disponibilidad depende del estado visible.",
      "Si se reclama un producto fisico, el usuario debe revisar los datos antes de confirmar.",
    ],
    screenshots: ["Seccion de recompensas en tienda", "Modal de reclamo", "Aviso de disponibilidad de gift cards"],
    faqs: [
      {
        question: "Las recompensas estan garantizadas?",
        answer: "No se debe afirmar eso. Los terminos las presentan como promocionales y sujetas a condiciones.",
      },
      {
        question: "Donde veo la disponibilidad?",
        answer: "Revisa la tienda y el estado visible de cada recompensa antes de intentar un reclamo.",
      },
    ],
    internalLinks: [
      { label: "Tienda", path: route("tienda") },
      { label: "Torneos", path: route("torneos") },
      { label: "Puzzles", path: route("puzzles") },
    ],
    cta: {
      label: "Ver tienda",
      path: route("tienda"),
    },
    breadcrumbs: [
      { label: "Tienda", path: route("tienda") },
      { label: "Recompensas", path: route("tienda/recompensas") },
    ],
    sources: [
      "docs/xima-knowledge/tienda-y-recompensas.md",
      "frontend/src/pages/user/Store.js",
      "frontend/src/constants/termsAndConditions.js",
      "backend/controllers/orderController.js",
    ],
    status: "ownerConfirmation",
  }),
  page({
    slug: "ajustes",
    friendlyUrl: canonical("ajustes"),
    title: "Ajustes de XIMA | Sonido, animaciones, camara, voz e idioma",
    metaDescription:
      "Aprende que puedes configurar en Ajustes de XIMA: sonido, animaciones, seguimiento de camara, velocidad de bloques, voz, idioma y soporte.",
    h1: "Ajustes de XIMA",
    intro:
      "Ajustes permite personalizar la experiencia de XIMA sin cambiar las reglas del tablero. Desde ahi se gestionan preferencias generales, idioma y soporte.",
    sections: [
      {
        heading: "Preferencias generales",
        body:
          "La pantalla incluye sonido del juego, animaciones, seguimiento de camara, velocidad de animacion de bloques y control por voz.",
      },
      {
        heading: "Idioma",
        body:
          "La interfaz permite elegir idiomas disponibles como espanol, ingles, ruso y aleman, usando el sistema de idioma del frontend.",
      },
      {
        heading: "Soporte",
        body:
          "La seccion de soporte permite completar email, asunto y mensaje para reportar problemas desde la propia aplicacion.",
      },
    ],
    examples: [
      "Desactiva animaciones si prefieres una experiencia mas directa.",
      "Activa seguimiento de camara si quieres que la vista siga la accion automaticamente.",
      "Usa Restablecer para volver a valores predeterminados visibles.",
    ],
    screenshots: ["Panel de Ajustes", "Preferencias generales", "Seccion Idioma"],
    faqs: [
      {
        question: "Los ajustes cambian las reglas?",
        answer: "No. Ajustes modifica preferencias de experiencia, no el objetivo ni las reglas del tablero.",
      },
      {
        question: "Donde esta soporte?",
        answer: "Dentro de Ajustes existe una seccion de soporte con formulario de contacto.",
      },
    ],
    internalLinks: [
      { label: "Idiomas", path: route("idiomas") },
      { label: "Control por voz", path: route("control-por-voz") },
      { label: "Soporte", path: route("soporte") },
    ],
    cta: {
      label: "Cambiar idioma",
      path: route("idiomas"),
    },
    breadcrumbs: [{ label: "Ajustes", path: route("ajustes") }],
    sources: ["docs/xima-knowledge/ajustes-e-idiomas.md", "frontend/src/pages/user/Settings.js", "frontend/src/services/language.js"],
  }),
  page({
    slug: "idiomas",
    friendlyUrl: canonical("idiomas"),
    title: "Idiomas en XIMA | Cambiar idioma de la interfaz",
    metaDescription:
      "Aprende como funciona la seleccion de idioma en XIMA y que idiomas aparecen en la interfaz actual.",
    h1: "Idiomas en XIMA",
    intro:
      "XIMA incluye seleccion de idioma desde Ajustes. El idioma afecta textos de la interfaz y ayuda a navegar la plataforma con mayor claridad.",
    sections: [
      {
        heading: "Idiomas visibles",
        body:
          "La pantalla de Ajustes muestra opciones para espanol, English, ruso y Deutsch mediante botones de idioma.",
      },
      {
        heading: "Recordatorio del idioma",
        body:
          "XIMA puede recordar el idioma elegido para que la interfaz siga mostrandose en esa opcion cuando vuelvas a navegar.",
      },
      {
        heading: "Guias por idioma",
        body:
          "Aunque la interfaz tenga varios idiomas, una guia de Aprender debe leerse natural y completa en el idioma que el jugador elija.",
      },
    ],
    steps: [
      "Abre Ajustes desde el menu.",
      "Entra en la seccion Idioma.",
      "Elige una opcion disponible.",
      "Verifica que la interfaz cambie sus textos.",
      "Guarda o continua navegando segun el comportamiento visible.",
    ],
    examples: [
      "Si juegas en movil, puedes entrar a idioma desde el boton de idioma del menu inferior.",
      "Si una guia aparece en otro idioma, debe ser clara para una persona que habla ese idioma.",
    ],
    screenshots: ["Lista de idiomas en Ajustes", "Boton de idioma en menu movil"],
    faqs: [
      {
        question: "Que idiomas aparecen?",
        answer: "La interfaz actual muestra espanol, English, ruso y Deutsch.",
      },
      {
        question: "Cambiar idioma modifica una partida?",
        answer: "No. Solo cambia la interfaz y textos disponibles.",
      },
    ],
    internalLinks: [
      { label: "Ajustes", path: route("ajustes") },
      { label: "Compatibilidad movil", path: route("compatibilidad-movil") },
      { label: "Soporte", path: route("soporte") },
    ],
    cta: {
      label: "Ver ajustes",
      path: route("ajustes"),
    },
    breadcrumbs: [
      { label: "Ajustes", path: route("ajustes") },
      { label: "Idiomas", path: route("idiomas") },
    ],
    sources: ["frontend/src/pages/user/Settings.js", "frontend/src/services/language.js", "frontend/src/layouts/GameLayout.js"],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "control-por-voz",
    friendlyUrl: canonical("control-por-voz"),
    title: "Control por voz en XIMA | Comandos, microfono y privacidad",
    metaDescription:
      "Aprende como activar el control por voz de XIMA para mover la vista, usar comandos de juego y permitir el microfono con seguridad.",
    h1: "Control por voz en XIMA",
    intro:
      "El control por voz permite usar la voz para ayudar a jugar XIMA, cambiar la vista del tablero, mover acciones compatibles y lanzar dados cuando el modo lo permite.",
    sections: [
      {
        heading: "Como se activa",
        body:
          "Activa Control por voz desde Ajustes o desde la pantalla donde aparezca la opcion. Para que funcione, acepta la solicitud de microfono que muestra tu navegador.",
      },
      {
        heading: "Que puedes hacer con la voz",
        body:
          "Puedes usar comandos para cambiar la vista del tablero, acercar o alejar la camara, centrar el tablero, confirmar acciones y lanzar dados en modos compatibles.",
      },
      {
        heading: "Privacidad",
        body:
          "Ajustes indica que no se guarda audio ni historial permanente de lo que dices para esta funcion. Activa el microfono solo cuando quieras usar voz.",
      },
    ],
    stepsTitle: "Como activarlo",
    steps: [
      "Abre Ajustes.",
      "Entra en preferencias generales.",
      "Pulsa Control de voz.",
      "Acepta el permiso de microfono cuando aparezca la solicitud del navegador.",
      "Di un comando relacionado con la vista, movimiento o dados segun lo que estes haciendo en XIMA.",
    ],
    examples: [
      "Puedes decir frases como acercar, alejar, centrar tablero o lanzar dados cuando esa accion tiene sentido en la partida.",
      "Si XIMA no reconoce tu voz, usa los controles manuales de la pantalla.",
    ],
    screenshots: ["Control de voz en Ajustes", "Permiso de microfono del navegador", "Estado de control de voz activo"],
    faqs: [
      {
        question: "El control por voz esta siempre disponible?",
        answer: "No. Depende del navegador, dispositivo, permiso de microfono y compatibilidad del reconocimiento de voz.",
      },
      {
        question: "Se guarda mi audio?",
        answer: "La interfaz de Ajustes indica que no se guarda audio ni historial permanente de transcripciones para esta funcion.",
      },
    ],
    internalLinks: [
      { label: "Ajustes", path: route("ajustes") },
      { label: "Chat de voz", path: route("chat-de-voz") },
      { label: "Solucion de problemas", path: route("solucion-de-problemas") },
    ],
    cta: {
      label: "Ver ajustes",
      path: route("ajustes"),
    },
    breadcrumbs: [{ label: "Control por voz", path: route("control-por-voz") }],
    sources: [
      "docs/xima-knowledge/control-por-voz.md",
      "frontend/src/pages/user/Settings.js",
      "frontend/src/services/voiceControl.js",
      "Xima_Game/Assets/Scripts/Core/VoiceCameraBridge.cs",
    ],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "chat-de-voz",
    friendlyUrl: canonical("chat-de-voz"),
    title: "Chat de voz en XIMA | Microfono y comunicacion en partida",
    metaDescription:
      "Aprende que se verifica del chat de voz de XIMA: botones por jugador, permiso de microfono, estados y limite de uso en partida.",
    h1: "Chat de voz en XIMA",
    intro:
      "El chat de voz permite comunicacion por microfono en partidas compatibles. La funcion usa permisos del navegador y controles visibles por jugador.",
    sections: [
      {
        heading: "Botones de voz",
        body:
          "El componente de voz muestra botones para jugadores y estados visuales de microfono, con acciones para activar o apagar cuando corresponde.",
      },
      {
        heading: "Permiso y disponibilidad",
        body:
          "Si el permiso del microfono se rechaza o falla, la interfaz muestra mensajes como permiso rechazado o no se pudo activar el microfono.",
      },
      {
        heading: "Uso responsable",
        body:
          "Los terminos advierten que el usuario debe evitar compartir informacion confidencial o sensible por voz.",
      },
    ],
    steps: [
      "Entra a una partida compatible.",
      "Busca la seccion de voz de jugadores.",
      "Pulsa tu boton de microfono.",
      "Acepta el permiso del navegador si aparece.",
      "Apaga el microfono cuando no quieras transmitir.",
    ],
    examples: [
      "Si el navegador bloquea el microfono, revisa permisos antes de reportar el problema.",
      "Si hay cuatro jugadores, la UI contempla espacios de voz para jugadores dentro del componente.",
    ],
    screenshots: ["Botones de voz de jugadores", "Estado de permiso de microfono", "Microfono activo en partida"],
    faqs: [
      {
        question: "Chat de voz y control por voz son lo mismo?",
        answer: "No. Control por voz ejecuta comandos; chat de voz sirve para comunicacion entre jugadores.",
      },
      {
        question: "Debo compartir datos personales por voz?",
        answer: "No. Evita contrasenas, datos de pago o informacion sensible.",
      },
    ],
    internalLinks: [
      { label: "Control por voz", path: route("control-por-voz") },
      { label: "Ajustes", path: route("ajustes") },
      { label: "Soporte", path: route("soporte") },
    ],
    cta: {
      label: "Ver control por voz",
      path: route("control-por-voz"),
    },
    breadcrumbs: [{ label: "Chat de voz", path: route("chat-de-voz") }],
    sources: [
      "docs/xima-knowledge/chat-de-voz.md",
      "frontend/src/components/TournamentVoiceChat.js",
      "frontend/src/constants/termsAndConditions.js",
    ],
    structuredDataType: "HowTo",
  }),
  page({
    slug: "compatibilidad-movil",
    friendlyUrl: canonical("compatibilidad-movil"),
    title: "Compatibilidad movil de XIMA | Menu, orientacion y tablero",
    metaDescription:
      "Aprende como XIMA adapta su interfaz a celulares: menu inferior, orientacion vertical u horizontal, tablero y navegacion.",
    h1: "Compatibilidad movil",
    intro:
      "XIMA adapta sus pantallas para celulares y tablets. En movil pueden cambiar el espacio del tablero, los menus visibles y la forma de navegar entre secciones.",
    sections: [
      {
        heading: "Menu movil",
        body:
          "En pantallas pequenas, algunas opciones pasan a una barra inferior, botones compactos o listas con desplazamiento para que puedas entrar a Lobby, Aprender, Historial, Ajustes y otras secciones.",
      },
      {
        heading: "Orientacion",
        body:
          "En posicion vertical hay menos ancho y algunas funciones pueden quedar contraidas o debajo de la pantalla. En posicion horizontal suele haber mas espacio para ver el tablero, replay o paneles laterales.",
      },
      {
        heading: "Carga del tablero",
        body:
          "El tablero necesita un navegador moderno y un dispositivo con recursos suficientes. Si no carga, conviene probar orientacion horizontal, cerrar pestanas pesadas o revisar la guia de solucion de problemas.",
      },
    ],
    stepsTitle: "Que cambia en movil",
    steps: [
      "En vertical, revisa la parte inferior porque algunos accesos pueden aparecer como botones compactos.",
      "En horizontal, usa el espacio extra para ver mejor el tablero, los replays y paneles de partida.",
      "Si una lista o panel parece cortado, desplaza la pantalla para ver las opciones ocultas.",
      "Si el tablero tarda en aparecer, espera la carga antes de tocar varios controles seguidos.",
      "Si el problema se repite, consulta Solucion de problemas con el modelo de telefono y navegador que usas.",
    ],
    examples: [
      "Si en movil no ves el menu lateral, busca la barra inferior con iconos.",
      "Si el tablero no carga, prueba cambiar la orientacion, actualizar el navegador o cerrar otras pestanas antes de reportar.",
    ],
    screenshots: ["Menu inferior movil", "Aprender en movil", "Vista de juego en orientacion vertical"],
    faqs: [
      {
        question: "XIMA funciona igual en movil y escritorio?",
        answer: "La experiencia se adapta, pero el espacio y los controles pueden cambiar por pantalla, orientacion y navegador.",
      },
      {
        question: "Que hago si el tablero no carga?",
        answer: "Usa la pagina de solucion de problemas y reporta el dispositivo, navegador y momento del fallo sin compartir datos sensibles.",
      },
    ],
    internalLinks: [
      { label: "Ajustes", path: route("ajustes") },
      { label: "Idiomas", path: route("idiomas") },
      { label: "Solucion de problemas", path: route("solucion-de-problemas") },
    ],
    cta: {
      label: "Ver solucion de problemas",
      path: route("solucion-de-problemas"),
    },
    breadcrumbs: [{ label: "Compatibilidad movil", path: route("compatibilidad-movil") }],
    sources: [
      "docs/xima-knowledge/compatibilidad-movil.md",
      "frontend/src/layouts/GameLayout.js",
      "frontend/src/pages/user/Lobby.js",
      "Xima_Game/Assets/Scripts/SamsungMaliWebGLMaterialFix.cs",
    ],
    structuredDataType: "Article",
  }),
  page({
    slug: "solucion-de-problemas",
    friendlyUrl: canonical("solucion-de-problemas"),
    title: "Solucion de problemas de XIMA | Replay, voz, movil y partidas",
    metaDescription:
      "Encuentra pasos seguros para revisar problemas comunes de XIMA: no carga, replay, microfono, partidas, amigos, tienda y soporte.",
    h1: "Solucion de problemas",
    intro:
      "Esta pagina organiza problemas comunes de usuario sin exponer informacion sensible ni datos privados.",
    sections: [
      {
        heading: "Antes de reportar",
        body:
          "Anota la seccion donde ocurrio el problema, el modo, el navegador, dispositivo y los pasos visibles. No compartas contrasenas, tokens ni datos sensibles.",
      },
      {
        heading: "Problemas frecuentes",
        body:
          "Los temas mas probables para soporte publico son carga del juego, replay no disponible, permisos de microfono, unirse a partidas, puzzles, tienda y ajustes de idioma.",
      },
      {
        heading: "Cuando usar soporte",
        body:
          "Si el problema se repite o bloquea la partida, usa el formulario de soporte con asunto claro y descripcion breve.",
      },
    ],
    steps: [
      "Identifica la seccion exacta: lobby, partida, replay, tienda, ajustes o soporte.",
      "Revisa si el problema depende de permisos, conexion o disponibilidad de datos.",
      "Intenta repetir el flujo una vez sin compartir informacion sensible.",
      "Consulta una guia relacionada dentro de Aprender.",
      "Si continua, envia un mensaje a soporte con contexto claro.",
    ],
    examples: [
      "Si un replay no abre, verifica primero que la partida tenga replay guardado.",
      "Si el microfono falla, revisa permisos del navegador antes de enviar soporte.",
      "Si una tienda muestra saldo insuficiente, revisa los bonos visibles en tu cuenta.",
    ],
    screenshots: ["Formulario de soporte", "Mensaje de permiso de microfono", "Estado de replay no disponible"],
    faqs: [
      {
        question: "Debo enviar mi contrasena para que me ayuden?",
        answer: "No. Nunca compartas contrasenas, tokens ni claves.",
      },
      {
        question: "Que informacion si ayuda a soporte?",
        answer: "Seccion, navegador, dispositivo, modo de juego, hora aproximada y descripcion del paso donde ocurrio el problema.",
      },
    ],
    internalLinks: [
      { label: "Soporte", path: route("soporte") },
      { label: "Compatibilidad movil", path: route("compatibilidad-movil") },
      { label: "Control por voz", path: route("control-por-voz") },
      { label: "Controles de replay", path: route("replay/controles") },
    ],
    cta: {
      label: "Contactar soporte",
      path: route("soporte"),
    },
    breadcrumbs: [{ label: "Solucion de problemas", path: route("solucion-de-problemas") }],
    sources: [
      "docs/xima-knowledge/soporte.md",
      "frontend/src/pages/user/Settings.js",
      "frontend/src/pages/user/Donate.js",
      "backend/routes/support.js",
    ],
    structuredDataType: "HowTo",
  }),
];

const nextHundredLearnPages = buildNextHundredLearnPages({ page, route, canonical });
const thirdHundredLearnPages = buildThirdHundredLearnPages({ page, route, canonical });
const bookLearnPages = buildBookLearnPages({ page, route, canonical });

export const learnSeoPages = [
  ...coreLearnSeoPages,
  ...nextHundredLearnPages,
  ...thirdHundredLearnPages,
  ...bookLearnPages,
];

export const learnSeoPagesBySlug = new Map();

learnSeoPages.forEach((learnPage) => {
  learnSeoPagesBySlug.set(learnPage.slug, learnPage);
  (learnPage.aliases || []).forEach((alias) => {
    learnSeoPagesBySlug.set(normalizeLearnContentPath(alias), learnPage);
  });
});

export const getLearnSeoPage = (rawSlug) => {
  const normalizedSlug = normalizeLearnContentPath(rawSlug);
  return learnSeoPagesBySlug.get(normalizedSlug) || null;
};

export const learnSeoNavigation = [
  { label: "Que es XIMA", path: route("que-es-xima") },
  { label: "Como jugar", path: route("como-jugar") },
  { label: "Reglas", path: route("reglas") },
  { label: "Tablero", path: route("reglas/tablero-y-casillas") },
  { label: "Bloque especial", path: route("reglas/bloque-especial") },
  { label: "Bloques", path: route("reglas/bloques") },
  { label: "Movimientos", path: route("reglas/movimientos") },
  { label: "Modos", path: route("modos-de-juego") },
  { label: "Ciencia", path: route("ciencia") },
  { label: "Azar", path: route("azar") },
  { label: "Cienzar", path: route("cienzar") },
  { label: "Ciencia V/S Azar", path: route("modos/ciencia-vs-azar") },
  { label: "FreeBox", path: route("modos/freebox") },
  { label: "Partidas online", path: route("partidas-online") },
  { label: "Crear partida", path: route("tutoriales/crear-partida") },
  { label: "Unirse a privada", path: route("tutoriales/unirse-partida-privada") },
  { label: "Lobby", path: route("lobby") },
  { label: "Historial y replay", path: route("historial-y-replay") },
  { label: "Anotacion", path: route("sistema-de-anotacion") },
  { label: "Controles replay", path: route("replay/controles") },
  { label: "Estadisticas", path: route("estadisticas") },
  { label: "Ranking", path: route("ranking") },
  { label: "Perfil", path: route("perfil") },
  { label: "Amigos", path: route("amigos") },
  { label: "Ver partidos", path: route("ver-partidos") },
  { label: "Torneos", path: route("torneos") },
  { label: "Puzzles", path: route("puzzles") },
  { label: "Bots", path: route("bots") },
  { label: "Tienda", path: route("tienda") },
  { label: "Ajustes", path: route("ajustes") },
  { label: "Control por voz", path: route("control-por-voz") },
  { label: "Chat de voz", path: route("chat-de-voz") },
  { label: "Movil", path: route("compatibilidad-movil") },
  { label: "Academia", path: route("academia") },
  { label: "Diccionario", path: route("diccionario") },
  { label: "Glosario", path: route("glosario") },
  { label: "Soporte", path: route("soporte") },
  { label: "Solucion", path: route("solucion-de-problemas") },
];

const learnNavItem = (slug) => {
  const learnPage = getLearnSeoPage(slug);
  const searchPrimaryText = learnPage
    ? [
        learnPage.slug,
        learnPage.h1,
        learnPage.title,
        ...(learnPage.breadcrumbs || []).map((breadcrumb) => breadcrumb.label),
      ]
        .filter(Boolean)
        .join(" ")
    : slug;
  const searchSecondaryText = learnPage
    ? [
        learnPage.metaDescription,
        learnPage.intro,
        ...(learnPage.sections || []).flatMap((section) => [section.heading, section.body]),
        ...(learnPage.internalLinks || []).map((link) => link.label),
        learnPage.cta?.label,
      ]
        .filter(Boolean)
        .join(" ")
    : slug;

  return {
    label: learnPage?.h1 || slug,
    path: learnPage?.path || route(slug),
    status: learnPage?.status || "verified",
    searchPrimaryText,
    searchSecondaryText,
  };
};

export const learnExpandedNavigationGroups = [
  {
    label: "Reglas y modos",
    items: [
      "reglas/incorporar-bloque",
      "reglas/mover-bloque",
      "reglas/subir-bloques",
      "reglas/rescate",
      "reglas/jugada-legal",
      "solucion/movimiento-no-permitido",
      "reglas/coordenadas",
      "reglas/cima",
      "reglas/objetivo",
      "reglas/turnos",
      "tutoriales/jugar-modo-ciencia",
      "tutoriales/jugar-modo-azar",
      "reglas/dados",
      "tutoriales/lanzar-dados",
      "modos/cienzar-ciencia-y-azar",
      "modos/contrazar",
    ].map(learnNavItem),
  },
  {
    label: "Partidas e interfaz",
    items: [
      "tutoriales/crear-partida-publica",
      "tutoriales/crear-partida-privada",
      "tutoriales/contrasena-de-partida",
      "tutoriales/seleccionar-modo",
      "tutoriales/tiempo-por-turno",
      "tutoriales/numero-de-jugadores",
      "partidas/independientes",
      "tutoriales/buscar-partidas",
      "interfaz/menu-principal",
      "interfaz/boton-aprender",
      "interfaz/volver-al-lobby",
      "interfaz/notificaciones-menu",
      "movil/menu-inferior",
      "movil/descargar-app",
    ].map(learnNavItem),
  },
  {
    label: "Historial y replay",
    items: [
      "historial",
      "tutoriales/actualizar-historial",
      "tutoriales/ver-replay",
      "replay/pausar-reproducir",
      "replay/reiniciar",
      "replay/saltar-final",
      "replay/camara-libre",
      "replay/seguimiento-camara",
      "replay/movil",
      "solucion/no-hay-replay",
    ].map(learnNavItem),
  },
  {
    label: "Estadisticas y ranking",
    items: [
      "estadisticas/cimax-ciencia",
      "estadisticas/cimax-cienzar",
      "estadisticas/cimax-contrazar",
      "estadisticas/cimax-azar",
      "estadisticas/torneos-ganados-por-modo",
      "estadisticas/tiempo-jugado",
      "estadisticas/win-rate",
      "estadisticas/racha-victorias",
      "estadisticas/bloques-capturados",
      "ranking/global",
      "ranking/por-modo",
      "ranking/ciencia",
      "ranking/cienzar",
      "ranking/contrazar",
      "ranking/azar",
      "ranking/jugadores-en-linea",
    ].map(learnNavItem),
  },
  {
    label: "Perfil y comunidad",
    items: [
      "perfil/codigo-de-jugador",
      "perfil/avatar",
      "tutoriales/editar-perfil",
      "perfil/estado-jugador",
      "perfil/amigo",
      "tutoriales/buscar-jugador",
      "tutoriales/aceptar-solicitud-amistad",
      "tutoriales/rechazar-solicitud-amistad",
      "tutoriales/cancelar-solicitud-amistad",
      "tutoriales/aceptar-invitacion-partida",
      "tutoriales/unirse-partida-amigo",
      "ver-partidos/en-vivo",
      "ver-partidos/tarjetas",
      "ver-partidos/refrescar",
      "noticias",
      "novedades",
    ].map(learnNavItem),
  },
  {
    label: "Torneos",
    items: [
      "tutoriales/crear-torneo",
      "torneos/sala-de-espera",
      "torneos/chat-sala",
      "torneos/invitar",
      "torneos/salir-sala",
      "torneos/jugar-desde-sala",
      "torneos/recompensas",
      "torneos/costo",
      "torneos/posiciones",
      "torneos/ganador",
      "torneos/activos",
      "torneos/futuros",
    ].map(learnNavItem),
  },
  {
    label: "Puzzles y bots",
    items: [
      "puzzles/modos",
      "puzzles/recompensas",
      "puzzles/tiempo",
      "puzzles/completado",
      "puzzles/fallido",
      "tutoriales/abrir-puzzle",
      "tutoriales/reintentar-puzzle",
      "tutoriales/siguiente-puzzle",
      "bots/modo-libre",
      "bots/azar",
      "bots/cienzar",
      "bots/ciencia-vs-azar",
      "bots/dificultad",
      "bots/dificultad-facil",
      "bots/dificultad-media",
      "bots/dificultad-dificil",
    ].map(learnNavItem),
  },
];

export const learnThirdHundredNavigationGroups = [
  {
    label: "Centros por modo",
    items: [
      "ciencia/como-se-juega",
      "ciencia/reglas",
      "ciencia/errores-frecuentes",
      "ciencia/analizar-partidas",
      "ciencia/entrenar-ia",
      "ciencia/faq",
      "azar/dados",
      "azar/probabilidad-decisiones",
      "azar/faq",
      "cienzar/ciencia-y-azar",
      "cienzar/faq",
    ].map(learnNavItem),
  },
  {
    label: "Academia",
    items: [
      "academia/principiantes",
      "academia/intermedio",
      "academia/lecciones",
      "academia/ejercicios",
      "academia/analisis-partidas",
    ].map(learnNavItem),
  },
  {
    label: "Diccionario aplicado",
    items: [
      "diccionario/estrategia",
      "diccionario/tactica",
      "diccionario/planificacion",
      "diccionario/anticipacion",
      "diccionario/riesgo",
      "diccionario/probabilidad",
      "diccionario/toma-de-decisiones",
      "diccionario/pensamiento-logico",
      "diccionario/ataque",
      "diccionario/defensa",
      "diccionario/ventaja",
      "diccionario/posicion",
      "diccionario/movimiento",
      "diccionario/objetivo",
      "diccionario/error",
      "diccionario/analisis",
    ].map(learnNavItem),
  },
  {
    label: "Articulos de estrategia",
    items: [
      "articulos/como-mejorar-la-toma-de-decisiones",
      "articulos/tactica-vs-estrategia",
      "articulos/aprender-de-una-derrota",
      "articulos/planificar-varios-movimientos",
      "articulos/errores-comunes-juegos-estrategia",
      "articulos/juegos-estrategia-pensamiento-logico",
      "articulos/inteligencia-artificial-juegos-de-mesa",
      "articulos/ciencia-y-azar-en-juegos",
      "articulos/analizar-partida-paso-a-paso",
    ].map(learnNavItem),
  },
  {
    label: "Solucion de problemas",
    items: [
      "solucion/unity-no-carga",
      "solucion/webgl-no-compatible",
      "solucion/microfono-bloqueado",
      "solucion/idioma-no-cambia",
      "solucion/no-puedo-crear-partida",
      "solucion/no-puedo-unirme-partida",
      "solucion/no-veo-amigos",
      "solucion/tienda-no-muestra-recompensas",
      "solucion/puzzle-no-avanza",
      "solucion/ranking-no-actualiza",
      "solucion/historial-vacio",
      "solucion/replay-no-reproduce",
      "solucion/dados-no-aparecen",
      "solucion/bot-no-inicia",
    ].map(learnNavItem),
  },
  {
    label: "Cuenta, tienda y recompensas",
    items: [
      "cuenta/crear-cuenta",
      "cuenta/iniciar-sesion",
      "cuenta/cerrar-sesion",
      "cuenta/terminos-y-condiciones",
      "cuenta/privacidad",
      "cuenta/codigo-de-jugador",
      "cuenta/seguridad",
      "tienda/bonos",
      "tienda/saldo",
      "tienda/comprar-skin",
      "tienda/equipar-skin",
      "tienda/gift-cards",
      "tienda/reclamar-recompensa",
    ].map(learnNavItem),
  },
  {
    label: "Voz, movil y accesibilidad",
    items: [
      "voz/comandos-camara",
      "voz/comandos-dados",
      "voz/comandos-movimiento",
      "voz/permisos-microfono",
      "movil/orientacion",
      "movil/webgl",
      "movil/menu-lobby",
      "movil/jugar-en-celular",
    ].map(learnNavItem),
  },
  {
    label: "Replay, estadisticas y ranking",
    items: [
      "replay/eventos",
      "replay/leer-anotacion",
      "replay/estudiar-errores",
      "replay/compartir-aprendizaje",
      "estadisticas/interpretar-cimax",
      "estadisticas/comparar-modos",
      "estadisticas/leer-progreso",
      "ranking/leer-tabla",
      "ranking/competir-por-modo",
    ].map(learnNavItem),
  },
  {
    label: "Comunidad, torneos y practica",
    items: [
      "amigos/privacidad",
      "amigos/invitaciones",
      "amigos/jugar-con-amigo",
      "amigos/solicitudes",
      "torneos/como-funcionan",
      "torneos/seguir-sala",
      "torneos/revisar-recompensas",
      "puzzles/como-entrenar",
      "puzzles/leer-objetivo",
      "puzzles/usar-recompensas",
      "bots/elegir-bot",
      "bots/practicar-ciencia",
      "bots/practicar-azar",
      "glosario/cimax",
      "glosario/replay",
    ].map(learnNavItem),
  },
];

const expandedNavigationGroupItemsByLabel = new Map(
  learnExpandedNavigationGroups.map((group) => [group.label, group.items])
);

const learnCompleteGroup = (label, coreSlugs, expandedGroupLabel = label) => ({
  label,
  items: [
    ...coreSlugs.map(learnNavItem),
    ...(expandedNavigationGroupItemsByLabel.get(expandedGroupLabel) || []),
  ],
});

export const learnBookNavigationGroups = BOOK_LEARN_NAVIGATION_GROUPS.map((group) => ({
  label: group.label,
  items: group.slugs.map(learnNavItem),
}));

export const learnCompleteNavigationGroups = [
  learnCompleteGroup("Primeros pasos", [
    "que-es-xima",
    "como-jugar",
    "academia",
    "diccionario",
    "glosario",
  ], null),
  learnCompleteGroup("Reglas y modos", [
    "reglas",
    "reglas/tablero-y-casillas",
    "reglas/bloque-especial",
    "reglas/bloques",
    "reglas/movimientos",
    "modos-de-juego",
    "ciencia",
    "azar",
    "cienzar",
    "modos/ciencia-vs-azar",
    "modos/freebox",
  ]),
  learnCompleteGroup("Partidas e interfaz", [
    "partidas-online",
    "tutoriales/crear-partida",
    "tutoriales/unirse-partida-privada",
    "partidas/publicas-y-privadas",
    "lobby",
  ]),
  learnCompleteGroup("Historial, replay y anotacion", [
    "historial-y-replay",
    "sistema-de-anotacion",
    "replay/controles",
    "replay/velocidad",
    "replay/timeline",
    "replays-destacados",
  ], "Historial y replay"),
  learnCompleteGroup("Estadisticas y ranking", [
    "estadisticas",
    "estadisticas/cimax",
    "ranking",
  ]),
  learnCompleteGroup("Perfil y comunidad", [
    "perfil",
    "amigos",
    "tutoriales/enviar-solicitud-amistad",
    "tutoriales/invitar-amigo-a-partida",
    "ver-partidos",
  ]),
  learnCompleteGroup("Torneos", [
    "torneos",
    "tutoriales/unirse-a-torneo",
  ]),
  learnCompleteGroup("Puzzles y bots", [
    "puzzles",
    "tutoriales/resolver-puzzle",
    "bots",
    "tutoriales/jugar-con-bot",
  ]),
  learnCompleteGroup("Tienda, ajustes y soporte", [
    "tienda",
    "tienda/skins",
    "tienda/recompensas",
    "ajustes",
    "idiomas",
    "control-por-voz",
    "chat-de-voz",
    "compatibilidad-movil",
    "soporte",
    "solucion-de-problemas",
  ], null),
  ...learnThirdHundredNavigationGroups,
  ...learnBookNavigationGroups,
];
